import EventEmitter from "eventemitter3";
import { Context } from "../../../../models/context";
import { Data } from "../../../../models/data";
import {
    IDataItem, IDataModifier, IDataModifierDisplayGroup, IDataPreModifier, IItemModifierDisplayGroupModifier,
    IItemPrice, IMenuItemPrice
} from "../../../../models/data.interface";
import { Order } from "../../../../models/order";
import { IOrderItem, IOrderItemModifier } from "../../../../models/order.interface";
import { IGetItemTaxoItem } from "../../../../online-ordering/interfaces/online-ordering.interface";
import { OnlineOrderingUtil } from "../../../../online-ordering/online-ordering-util";
import { ItemPricingUnavailableError } from "../../../../online-ordering/errors/item-pricing-unavailable-error";
import { DataLayerService } from "../../../../services/data-layer-service";
import { LocalizationService } from "../../../../services/localization-service";
import { CalorieDisplay, CouponIdList, CouponTypeList, CustomizeItemUiOption, HalfModifierSelection } from "../../../../types/enums";
import { DialogCreators } from "../../../../utils/dialog-creators";
import { Util } from "../../../../utils/util";
import { IDealBuilderCustomizeItem } from "../../DealBuilder/deal-builder.interface";
import { ICustomizeItemInstruction } from "../customize-item.interface";
import { EventTypes } from "../../../../core/event-bus";
import { IApplyCouponByIdParamsPartial, IRemovedCoupon } from "../../../../models/coupons.interface";
import { ProfileService } from "../../../../services/profile-service";
import { IName } from "../../../../interfaces/global.interfaces";
import {
    CustomizeItemHalfCustomization,
    CustomizeItemModifier,
    CustomizeItemModifierGroup,
    CustomizeItemModifierResult,
    ICustomizeItemBaseItem, ICustomizeItemCore,
    ICustomizeItemEditItemModifierMulti,
    IMergedModifier,
    IMergedModifierGroup,
    ModifierRenderState, ModifierSelectionInput, ModifierSelectionResult
} from "./customize-item-core.interface";
import { Coupon } from '../../../../models/coupons';

export class CustomizeItemCore {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _context: Context;
    private readonly _profileService: ProfileService;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayer: DataLayerService;
    private _item: ICustomizeItemCore;

    constructor(eventBus: EventEmitter, data: Data, order: Order, context: Context, profileService: ProfileService, localizationService: LocalizationService, dataLayer: DataLayerService) {
        this._eventBus = eventBus;
        this._data = data;
        this._order = order;
        this._context = context;
        this._localizationService = localizationService;
        this._profileService = profileService;
        this._dataLayer = dataLayer;

        this._item = {} as ICustomizeItemCore;
        this._eventBusListeners();
    }

    /**
     * Retrieves the first base item with modifiers for the whole and left half.
     * @returns {ICustomizeItemBaseItem} The first base item with its modifiers.
     */
    get firstItem(): ICustomizeItemBaseItem {
        const modifiers = this._getModifiersByHalf(HalfModifierSelection.LEFT);
        this._item.baseItem.ItemId = this._item.itemState.get(HalfModifierSelection.LEFT)?.item.ItemId ?? 0;
        this._item.baseItem.Modifiers = modifiers;

        return this._item.baseItem;
    }

    /**
     * Retrieves the second base item with modifiers for the whole and right half.
     * @returns {ICustomizeItemBaseItem} The second base item with its modifiers.
     */
    get secondItem(): ICustomizeItemBaseItem {
        const modifiers = this._getModifiersByHalf(HalfModifierSelection.RIGHT);
        this._item.baseItem2.ItemId = this._item.itemState.get(HalfModifierSelection.RIGHT)?.item.ItemId ?? 0;
        this._item.baseItem2.Modifiers = modifiers;

        return this._item.baseItem2;
    }

    get sizeId(): number {
        return this._item.sizeId;
    }

    get quantity(): number {
        return this._item.quantity;
    }

    get modifiers(): CustomizeItemModifier[] {
        return this._getAllModifiers();
    }

    get instructions(): ICustomizeItemInstruction[] {
        return this._item.instructions;
    }

    get mealDeal(): IDealBuilderCustomizeItem | null {
        return this._item.mealDeal;
    }

    get recentOrder(): boolean {
        return this._item.recentOrder;
    }

    get elementIndex(): number {
        return this._item.elementIndex;
    }

    get isHalfMode(): boolean {
        return this._item.isHalfMode;
    }

    get defaultPreModifierId(): number {
        return this._item.defaultPreModifierId;
    }

    public set isHalfMode(value: boolean) {
        this._item.isHalfMode = value;
    }

    public set quantity(quantity: number) {
        this._item.quantity = quantity;
    }

    /**
     * Checks whether the controller is in editing mode.
     * Editing mode is active if there are items to edit.
     *
     * @returns {boolean} True if editing, false otherwise.
     */
    public isEditing(): boolean {
        return this._item.itemsToEdit.length > 0;
    }

    /**
     * Checks if the item is a half-and-half selection.
     * @returns {boolean} True if the item is a half-and-half selection, false otherwise.
     */
    public isHalfHalf = (): boolean => {
        const modifiers = this._getAllModifiers();
        return (this._item.isHalfMode
            && !this._areItemsEqual(this.firstItem, this.secondItem)) || modifiers.some(x => [HalfModifierSelection.LEFT, HalfModifierSelection.RIGHT].includes(x.halfId));
    }

    /**
     * Updates the current item's size ID.
     *
     * @param {number} sizeId - The new size ID to assign.
     */
    public readonly setSizeId = (sizeId: number): void => {
        this._item.sizeId = sizeId;
    }

    /**
     * Initializes or updates the item customization model with provided parameters.
     * If editing an existing item, properties like quantity and sizeId are applied from it.
     *
     * @param {number} itemId - The base item ID.
     * @param {number} sizeId - The size ID to apply.
     * @param {number} quantity - The item quantity.
     * @param {IOrderItem[]} itemsToEdit - List of existing order items being edited.
     * @param {IDealBuilderCustomizeItem | null} mealDeal - Associated meal deal (if any).
     * @param {number} [elementIndex=0] - The element index (defaults to 0).
     */
    public setParams(
        itemId: number,
        sizeId: number,
        quantity: number,
        itemsToEdit: IOrderItem[],
        mealDeal: IDealBuilderCustomizeItem | null,
        recentOrder: boolean,
        elementIndex?: number
    ): void {
        const itemToEdit = itemsToEdit[0];
        const tempItemId = itemToEdit?.ItemId ?? itemId;

        const menuItem = this._getMenuItemById(tempItemId);
        if (!menuItem)
            return;

        this._item = this._createModel(menuItem, sizeId, quantity, itemsToEdit, mealDeal, recentOrder, elementIndex);

        if (itemToEdit) {
            // Apply properties from existing item
            this.quantity = itemToEdit.Quantity;
            this.setSizeId(itemToEdit.SizeId);
            this._item.instructions = itemToEdit.Instructions;

            // Handle second half if present
            const secondItemToEdit = itemsToEdit[1];
            if (secondItemToEdit) {
                const menuItem2 = this._getMenuItemById(secondItemToEdit.ItemId);
                this._getHalf(HalfModifierSelection.RIGHT).item = menuItem2 ?? menuItem;

                if (this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.MULTI_ITEM)
                    this.isHalfMode = true;
            }
        }
    }

    /**
     * Retrieves the menu item associated with a given half.
     *
     * @param {HalfModifierSelection} halfId - The half identifier (LEFT, RIGHT, or WHOLE).
     * @returns {IDataItem} The menu item for the specified half.
     * @throws {Error} If the half is not initialized.
     */
    public getMenuItem = (halfId: HalfModifierSelection): IDataItem => {
        if (halfId === HalfModifierSelection.RIGHT)
            return this._getHalf(HalfModifierSelection.RIGHT).item;
        
        // Default to LEFT for LEFT and WHOLE
        return this._getHalf(HalfModifierSelection.LEFT).item;
    }

    public setMenuItem(halfId: HalfModifierSelection, menuItem: IDataItem): void {
        if (halfId === HalfModifierSelection.RIGHT)
            this._getHalf(HalfModifierSelection.RIGHT).item = menuItem;
        else
            this._getHalf(HalfModifierSelection.LEFT).item = menuItem;
    }

    /**
     * Calculates and updates pricing for the selected item(s).
     * Updates menu price, tax values, and triggers recalculation for half-half scenarios.
     * Emits price and calorie change events on the event bus.
     */
    public recalculateItemPricing(): void {
        const items = this._getItems();
        for (const item of items) {
            const menuItem = this.getMenuItem(item.halfId);
            // Live preview only -- leave the previous price in place on failure rather than
            // breaking modal interactivity; the authoritative block is at add-to-cart time.
            let itemPrice: IMenuItemPrice;
            try {
                itemPrice = this._order._getItemPrice(menuItem, this._item.sizeId, item.item.Modifiers);
            } catch (e) {
                if (!(e instanceof ItemPricingUnavailableError))
                    throw e;
                continue;
            }
            const price = Math.abs(itemPrice.Price);
            item.item.MenuPrice = price;
            item.item.MenuPriceTax = itemPrice.Tax;
            item.item.Tax = itemPrice.Tax;
            item.item.PriceIncludesTax = itemPrice.TaxIncluded
            item.item.Price = price;
            item.item.MinPrice = itemPrice.MinPrice;
            item.item.HalfHalfPrice = price;
        }

        if (this.isHalfHalf())
            this._calculateHalfHalfPricing();

        this._taxSelectedItem();

        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_PRICE_CHANGE, this.firstItem.HalfHalfPrice);
        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_CALORIES_CHANGE, this.firstItem.Modifiers, this.secondItem.Modifiers);
    }

    /**
     * Updates the selection state of a modifier, applying pre-modifier rules
     * and ensuring proper half/whole logic.
     *
     * @param {ModifierSelectionInput} input - Modifier selection details including IDs and selection state.
     * @returns {ModifierSelectionResult} - Result indicating success, whether to revert checkbox, and final halfId.
     */
    public updateModifierSelection(input: ModifierSelectionInput): ModifierSelectionResult {
        const { left, right, isWhole } = this._getModifierData(input.modifierId);
        const displayGroup = this._data.getProperty("ModifierDisplayGroups").find(x => x.ModifierDisplayGroupId === input.displayGroupId);

        if (!input.isMultiSelect && left?.isSelected && !displayGroup?.MustToggle) {
            return { success: false, forceRevertCheckbox: false, halfId: HalfModifierSelection.WHOLE };
        }

        let preModifierId = input.isCheckboxCurrentlyChecked
            ? this._item.nonePreModifierId
            : this._item.defaultPreModifierId;

        const modifier = input.halfId === HalfModifierSelection.RIGHT ? right : left;
        if (!input.isCheckboxCurrentlyChecked && displayGroup?.IsWeightChangeable && isWhole && modifier && modifier.preModifierId !== this._item.nonePreModifierId)
            preModifierId = modifier.preModifierId;

        const modifierAdded = this.updateSelectedItemModifier(
            input.displayGroupId,
            input.modifierId,
            preModifierId,
            input.halfId,
            false,
            !input.isCheckboxCurrentlyChecked,
            input.isCheckboxCurrentlyChecked
        );

        return {
            success: modifierAdded,
            forceRevertCheckbox: !modifierAdded,
            halfId: modifier?.halfId ?? HalfModifierSelection.WHOLE
        };
    }

    /**
     * Validates whether a size change is allowed for the current item(s).
     * Checks half-half rules, modifier compatibility, and size availability.
     *
     * @param {number} sizeId - The proposed new size ID.
     * @returns {{ valid: boolean, sizeId: number }} - Result containing validity and the valid sizeId
     *                                                (or current sizeId if invalid).
     */
    public validateSizeChange(sizeId: number): { valid: boolean, sizeId: number } {
        const menuItem1 = this.getMenuItem(HalfModifierSelection.LEFT);
        const menuItem2 = this.getMenuItem(HalfModifierSelection.RIGHT);

        if (this.isHalfHalf()) {
            const firstValid = this._checkItemModifiersForSizes(menuItem1, this.firstItem.Modifiers, sizeId);
            const secondValid = this._checkItemModifiersForSizes(menuItem2, this.secondItem.Modifiers ?? [], sizeId);
            const sizeValid = this._checkSizeIsHalfable(menuItem1, sizeId);

            if (!sizeValid || !firstValid || !secondValid) {
                return { valid: false, sizeId: this._item.sizeId };
            }
        } else {
            const firstValid = this._checkItemModifiersForSizes(menuItem1, this.firstItem.Modifiers, sizeId);
            if (!firstValid) {
                return { valid: false, sizeId: this._item.sizeId };
            }
        }

        return { valid: true, sizeId: -1 };
    }

    /**
     * Applies a size change to the current item(s) if valid.
     * Re-prices items after updating size and recalculates pricing for both halves if applicable.
     *
     * @param {number} sizeId - The new size ID to apply.
     * @returns {boolean} True if the size change was applied successfully, false otherwise.
     */
    public applySizeChange(sizeId: number): boolean {
        const isHalfHalf = this.isHalfHalf();
        const modifiers = isHalfHalf ? this.secondItem.Modifiers : this.firstItem.Modifiers;
        const halfId = isHalfHalf ? HalfModifierSelection.RIGHT : HalfModifierSelection.LEFT;
        const menuItem = this.getMenuItem(halfId);

        let priceCheck: IMenuItemPrice;
        try {
            priceCheck = this._order._getItemPrice(menuItem, this._item.sizeId, modifiers);
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;
            return false;
        }
        if (priceCheck.Price < 0)
            return false;

        this.setSizeId(sizeId);
        this.recalculateItemPricing();

        return true;
    }

    /**
     * Determines whether the current item size supports half-and-half selection.
     *
     * @param {IDataItem} menuItem - The menu item to check.
     * @returns {boolean} True if the item can be split into halves for the current size/order type; otherwise false.
     */
    public isSizeHalfable(menuItem: IDataItem): boolean {
        if (!menuItem.IsHalfable)
            return false;

        const orderTypeId = this._order.getProperty("OrderTypeID");
        const sizes = menuItem.OrderTypes.find(x => x.OrderTypeId === orderTypeId)?.Sizes;

        return sizes?.find(x => x.SizeId === this._item.sizeId)?.IsHalfable ?? false;
    }

    public isItemHalfable(): boolean {
        const menuItem = this.getMenuItem(HalfModifierSelection.LEFT);
        const menuItem2 = this.getMenuItem(HalfModifierSelection.RIGHT);
        return menuItem.IsHalfable && menuItem2.IsHalfable;
    }

    /**
     * Applies a change in half/whole selection for a modifier.
     * Handles updating pre-modifier IDs for left, right, and whole selections,
     * and recalculates pricing after the change.
     *
     * @param {number} modifierDisplayGroupId - The display group ID of the modifier.
     * @param {number} modifierId - The modifier ID being updated.
     * @param {HalfModifierSelection} halfId - The half to apply the change to (LEFT, RIGHT, WHOLE).
     */
    public applyHalfSelectionChange(modifierDisplayGroupId: number, modifierId: number, halfId: HalfModifierSelection): void {
        const data = this._getModifierData(modifierId);

        // Determine pre-modifier ID: use existing if available, fallback to "none"
        const preModifierId =
            [data.left, data.right].find(x => x?.preModifierId !== this._item.nonePreModifierId)?.preModifierId
            ?? this._item.nonePreModifierId;

        switch (halfId) {
            case HalfModifierSelection.WHOLE:
                this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, preModifierId, halfId, false, true);
                break;
            case HalfModifierSelection.LEFT:
                // Clear right side, set left side
                this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, this._item.nonePreModifierId, HalfModifierSelection.RIGHT, false, false, true);
                this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, preModifierId, HalfModifierSelection.LEFT, false, true);
                break;
            case HalfModifierSelection.RIGHT:
                // Clear left side, set right side
                this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, this._item.nonePreModifierId, HalfModifierSelection.LEFT, false, false, true);
                this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, preModifierId, HalfModifierSelection.RIGHT, false, true);
                break;
        }

        this.recalculateItemPricing();
    }

    /**
     * Applies a pre-modifier change to the given modifier, adjusting the half selection if needed.
     * A display group with Half & Half turned off always moves both halves together.
     *
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @param {number} modifierId - The ID of the modifier being updated.
     * @param {HalfModifierSelection} halfId - The half selection (left, right, or whole).
     * @param {number} preModifierId - The ID of the new pre-modifier to apply.
     */
    public updatePreModifier(modifierDisplayGroupId: number, modifierId: number, halfId: HalfModifierSelection, preModifierId: number): void {
        const data = this._getModifierData(modifierId);
        const modifierDisplayGroup = this.getModifierDisplayGroupById(modifierDisplayGroupId);

        // Bug 82952: a group that is not halfable can never sit on a single half, so a pre-modifier
        // change has to apply to the whole item - otherwise toggling the modifier off and back on
        // leaves the other half on the "none" pre-modifier (e.g. a half arriving with No Red Sauce).
        // Both halves must hold the modifier, or updateSelectedItemModifier cannot resolve them.
        if (modifierDisplayGroup && !modifierDisplayGroup.IsHalfable && data.left && data.right) {
            halfId = HalfModifierSelection.WHOLE;
        } else if (!this.isHalfMode) {
            if (data.isWhole)
                halfId = HalfModifierSelection.WHOLE;
            else if (data.right?.isSelected)
                halfId = data.right.halfId;
            else if (data.left?.isSelected)
                halfId = data.left.halfId;
        }

        this.updateSelectedItemModifier(modifierDisplayGroupId, modifierId, preModifierId, halfId, false, false);
    }

    public resetHalfModifiersState(halfId: HalfModifierSelection): void {
        const halfData = this._getHalf(halfId);

        for (const group of halfData.modifierGroups.values()) {
            group.modifiers.clear();
        }
    }

    public cloneHalfState(removeHalfId: HalfModifierSelection): void {
        const keepHalfId = removeHalfId === HalfModifierSelection.RIGHT
            ? HalfModifierSelection.LEFT
            : HalfModifierSelection.RIGHT;

        const modifiers = this._getAllModifiersByHalf(keepHalfId)
        modifiers.forEach(x => x.halfId = HalfModifierSelection.WHOLE)

        const keepHalfData = this._getHalf(keepHalfId);
        const cloned = this._cloneHalfState(keepHalfData);

        this._item.itemState.set(removeHalfId, cloned);
    }

    private _cloneHalfState(source: CustomizeItemHalfCustomization): CustomizeItemHalfCustomization {
        const clonedGroups = new Map<number | null, CustomizeItemModifierGroup>();

        for (const [key, group] of source.modifierGroups) {
            clonedGroups.set(key, this._cloneModifierGroup(group));
        }

        return {
            item: source.item, // shared reference is fine
            modifierGroups: clonedGroups
        };
    }

    private _cloneModifierGroup(group: CustomizeItemModifierGroup): CustomizeItemModifierGroup {
        const clonedModifiers = new Map<number, CustomizeItemModifier>();

        for (const [modId, state] of group.modifiers) {
            clonedModifiers.set(modId, { ...state });
        }

        return {
            displayGroup: group.displayGroup,
            modifiers: clonedModifiers
        };
    }

    /**
     * Creates the render state for all modifiers in a display group.
     *
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The display group that the modifiers belong to.
     * @param {IMergedModifier[]} mergedModifiers - The list of modifiers for the display group.
     * @param {number | null} selectedPreModifierId - The currently selected pre-modifier ID, or null if none.
     * @param {boolean} isMultiSelect - Whether the display group allows multiple selections.
     * @returns {ModifierRenderState[]} A list of render states for each modifier.
     */
    public getModifierRenderState(
        modifierDisplayGroup: IDataModifierDisplayGroup,
        mergedModifiers: IMergedModifier[],
        selectedPreModifierId: number | null,
        isMultiSelect: boolean,
        firstRender: boolean = false
    ): ModifierRenderState[] {
        const result: ModifierRenderState[] = [];
        const preModifierId = selectedPreModifierId ?? this._item.defaultPreModifierId;
        const modifiers = this._data.getProperty("Modifiers");

        for (const merged of mergedModifiers) {
            const modifierId = merged.leftModifier?.ModifierId ?? merged.rightModifier?.ModifierId ?? null;

            if (modifierId === null)
                continue;

            const modifierData = modifiers.find(x => x.ModifierId === modifierId);
            if (!modifierData)
                continue;

            const renderState: ModifierRenderState = {
                leftModifier: merged.leftModifier,
                rightModifier: merged.rightModifier,
                modifierData,
                selectedData: new Map()
            };

            // ---- 1. Handle EDITING MODE
            if (this.isEditing() && firstRender) {
                this._applyEditingRenderState(
                    renderState,
                    modifierDisplayGroup,
                    merged,
                    modifierId,
                    preModifierId,
                    isMultiSelect
                );
            } else {
                this._applyNonEditingRenderState(
                    renderState,
                    modifierDisplayGroup,
                    merged,
                    modifierId,
                    preModifierId
                );
            }

            // Fallback WHOLE state (non-half mode only)
            if (renderState.selectedData.size === 0 && !this.isHalfMode) {
                renderState.selectedData.set(HalfModifierSelection.WHOLE, {
                    preModifierId: preModifierId,
                    isSelected: merged.leftModifier?.IsDefault ?? false,
                    isDisabled: false,
                    menuItem: this.getMenuItem(HalfModifierSelection.WHOLE)
                });
            }

            result.push(renderState);
        }

        return result;
    }

    /**
     * Applies modifier render state when the item is in editing mode.
     * Restores existing modifier selections if present; otherwise initializes
     * per-half or whole modifier state depending on half mode.
     *
     * @param {ModifierRenderState} renderState - Render state container to populate.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - Display group metadata for the modifier.
     * @param {IMergedModifier} merged - Merged left/right modifier data.
     * @param {number} modifierId - Modifier identifier.
     * @param {number} preModifierId - Active pre-modifier identifier.
     * @param {boolean} isMultiSelect - Indicates whether the display group allows multiple selections.
     * @returns {void}
     */
    private _applyEditingRenderState(
        renderState: ModifierRenderState,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        merged: IMergedModifier,
        modifierId: number,
        preModifierId: number,
        isMultiSelect: boolean
    ): void {
        const data =
            this._getEditItemModifierInfoMulti(
                modifierDisplayGroup.ModifierDisplayGroupId,
                modifierId
            );

        if (data) {
            for (const [key, value] of data.modifierData) {
                if (!value) {
                    this._applyEditHalfModifierState(
                        key,
                        renderState,
                        merged,
                        modifierDisplayGroup,
                        modifierId,
                        preModifierId
                    );
                    continue;
                }

                const modifier =
                    key === HalfModifierSelection.RIGHT
                        ? merged.rightModifier
                        : merged.leftModifier;

                const isDefault = modifier?.IsDefault ?? false;

                const isSelected =
                    (!isMultiSelect && !modifierDisplayGroup.IsHalfable)
                    || (isDefault && value.preModifierId !== this._item.nonePreModifierId)
                    || (!isDefault && value.preModifierId !== this._item.nonePreModifierId);

                this._createModifier(
                    key,
                    modifierDisplayGroup,
                    data.id,
                    value.preModifierId,
                    isSelected,
                    isDefault,
                    value.modifier
                );

                renderState.selectedData.set(key, {
                    preModifierId: value.preModifierId,
                    isSelected,
                    isDisabled: !modifier,
                    menuItem: this.getMenuItem(key)
                });
            }

            return;
        }

        if (this.isHalfMode) {
            this._applyEditHalfModifierState(
                HalfModifierSelection.LEFT,
                renderState,
                merged,
                modifierDisplayGroup,
                modifierId,
                preModifierId
            );

            this._applyEditHalfModifierState(
                HalfModifierSelection.RIGHT,
                renderState,
                merged,
                modifierDisplayGroup,
                modifierId,
                preModifierId
            );

            return;
        }

        const isDefault = merged.leftModifier?.IsDefault ?? false;

        this._createModifier(
            HalfModifierSelection.WHOLE,
            modifierDisplayGroup,
            modifierId,
            preModifierId,
            false,
            isDefault,
            null
        );

        renderState.selectedData.set(HalfModifierSelection.WHOLE, {
            preModifierId,
            isSelected: false,
            isDisabled: false,
            menuItem: this.getMenuItem(HalfModifierSelection.WHOLE)
        });
    }

    /**
     * Applies modifier render state when the item is not in editing mode.
     * Initializes modifier state based on half mode and existing modifier data,
     * creating default modifier entries when necessary.
     *
     * @param {ModifierRenderState} renderState - Render state container to populate.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - Display group metadata for the modifier.
     * @param {IMergedModifier} merged - Merged left/right modifier data.
     * @param {number} modifierId - Modifier identifier.
     * @param {number} preModifierId - Active pre-modifier identifier.
     * @returns {void}
     */
    private _applyNonEditingRenderState(
        renderState: ModifierRenderState,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        merged: IMergedModifier,
        modifierId: number,
        preModifierId: number
    ): void {
        if (this.isHalfMode) {
            const { left, right } = this._getModifierData(modifierId);

            this._applyHalfModifierState(
                HalfModifierSelection.LEFT,
                renderState,
                merged,
                left,
                modifierDisplayGroup,
                modifierId,
                preModifierId
            );

            this._applyHalfModifierState(
                HalfModifierSelection.RIGHT,
                renderState,
                merged,
                right,
                modifierDisplayGroup,
                modifierId,
                preModifierId
            );

            return;
        }

        const { left } = this._getModifierData(modifierId);

        if (left) {
            renderState.selectedData.set(HalfModifierSelection.WHOLE, {
                preModifierId: left.preModifierId,
                isSelected: left.isSelected,
                isDisabled: false,
                menuItem: this.getMenuItem(HalfModifierSelection.WHOLE)
            });
            return;
        }

        const isDefault = merged.leftModifier?.IsDefault ?? false;

        this._createModifier(
            HalfModifierSelection.WHOLE,
            modifierDisplayGroup,
            modifierId,
            preModifierId,
            isDefault,
            isDefault,
            null
        );
    }

    private _applyHalfModifierState(
        halfId: HalfModifierSelection,
        renderState: ModifierRenderState,
        merged: IMergedModifier,
        existing: CustomizeItemModifier | null,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        modifierId: number,
        preModifierId: number
    ): void {
        const halfModifier =
            halfId === HalfModifierSelection.LEFT
                ? merged.leftModifier
                : merged.rightModifier;

        if (existing) {
            renderState.selectedData.set(halfId, {
                preModifierId: existing.preModifierId,
                isSelected: existing.isSelected,
                isDisabled: halfModifier === null,
                menuItem: this.getMenuItem(halfId)
            });
            return;
        } 

        const isDefault = halfModifier?.IsDefault ?? false;

        this._createModifier(
            halfId,
            modifierDisplayGroup,
            modifierId,
            preModifierId,
            isDefault,
            isDefault,
            null
        );

        renderState.selectedData.set(halfId, {
            preModifierId: preModifierId,
            isSelected: halfModifier?.IsDefault ?? false,
            isDisabled: halfModifier === null,
            menuItem: this.getMenuItem(halfId)
        });
    }

    private _applyEditHalfModifierState(
        halfId: HalfModifierSelection,
        renderState: ModifierRenderState,
        merged: IMergedModifier,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        modifierId: number,
        preModifierId: number
    ): void {
        const halfModifier =
            halfId === HalfModifierSelection.LEFT
                ? merged.leftModifier
                : merged.rightModifier;

        const isDefault = halfModifier?.IsDefault ?? false;
        const isSelected = isDefault && preModifierId !== this._item.nonePreModifierId;

        this._createModifier(
            halfId,
            modifierDisplayGroup,
            modifierId,
            preModifierId,
            isSelected,
            isDefault,
            null
        );

        renderState.selectedData.set(halfId, {
            preModifierId: preModifierId,
            isSelected: isSelected,
            isDisabled: halfModifier === null,
            menuItem: this.getMenuItem(halfId)
        });
    }

    /**
     * Applies default modifiers to the current item based on
     * its size, configuration, and modifier display groups.
     *
     * Ensures that default selections are applied correctly and
     * respects single-choice group constraints.
     */
    public applyDefaultModifiers(): void {
        const leftItem = this.getMenuItem(HalfModifierSelection.LEFT);
        const rightItem = this.getMenuItem(HalfModifierSelection.RIGHT);

        this._applyDefaultsForSide(HalfModifierSelection.LEFT);
        this._applyDefaultsForSide(HalfModifierSelection.RIGHT);
        this._normalizeNonHalfableModifiers(leftItem, rightItem);
        this._updateModifiersPreModifiers();
    }

    private _applyDefaultsForSide(side: HalfModifierSelection): void {
        const menuItem = this.getMenuItem(side);
        const displayGroups = this._data.getProperty("ModifierDisplayGroups");

        const defaults = this._itemGetDefaultModifiers(
            menuItem,
            this._item.sizeId,
            displayGroups,
            this._item.defaultPreModifierId
        );

        for (const defaultModifier of defaults) {
            const { left, right } = this._getModifierData(defaultModifier.ModifierId);
            const data = side === HalfModifierSelection.LEFT ? left : right;

            if (!data) {
                const displayGroup = displayGroups.find(x => x.ModifierDisplayGroupId === defaultModifier.ModifierDisplayGroupId);
                if (!displayGroup)
                    continue;

                this._createModifier(
                    side,
                    displayGroup,
                    defaultModifier.ModifierId,
                    defaultModifier.PreModifierId,
                    true,
                    true,
                    defaultModifier
                );
            } else 
                data.modifier ??= defaultModifier;
        }
    }

    private _normalizeNonHalfableModifiers(
        leftItem: IDataItem,
        rightItem: IDataItem
    ): void {
        const displayGroups = this._data.getProperty("ModifierDisplayGroups");

        const nonHalfableGroups = displayGroups.filter(g => !g.IsHalfable);

        for (const group of nonHalfableGroups) {
            const leftGroup = leftItem.ModifierDisplayGroups
                ?.find(g => g.ModifierDisplayGroupId === group.ModifierDisplayGroupId);

            const rightGroup = rightItem.ModifierDisplayGroups
                ?.find(g => g.ModifierDisplayGroupId === group.ModifierDisplayGroupId);

            if (!leftGroup || !rightGroup)
                continue;

            const leftById = new Map(
                leftGroup.Modifiers.map(m => [m.ModifierId, m])
            );

            const rightById = new Map(
                rightGroup.Modifiers.map(m => [m.ModifierId, m])
            );

            const modifierIds = new Set<number>([
                ...leftById.keys(),
                ...rightById.keys()
            ]);

            for (const modifierId of modifierIds) {
                const { left, right } = this._getModifierData(modifierId);

                if (!left || !right)
                    continue;

                if (left.isSelected !== right.isSelected) {
                    left.isSelected = right.isSelected = false;
                    left.modifier = right.modifier = null;
                }
            }
        }
    }

    /**
     * Determines whether a modifier display group allows only a single choice.
     *
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The modifier display group to evaluate.
     * @returns {boolean} `true` if only one modifier can be selected, otherwise `false`.
     */
    public isSingleChoiceGroup(modifierDisplayGroup: IDataModifierDisplayGroup): boolean {
        return modifierDisplayGroup.MaxSelectionCount === 1 || modifierDisplayGroup.MustToggle;
    }

    /**
     * Calculates the formatted price and localized calorie information for a given size.
     *
     * @param {number} sizeId - The size ID for which to calculate price and calories.
     * @returns {{ price: string, calories: string | null }} 
     *          The formatted price and calorie label (or `null` if not available).
     */
    public calculatePriceAndCaloriesForSize(sizeId: number): { price: string, calories: string | null } {
        const menuItem = this.getMenuItem(HalfModifierSelection.WHOLE);
        const orderTypeId = this._order.getProperty("OrderTypeID");
        const country = this._data.getProperty("StoreInfo").country;

        const caloriesNames = this.calculateItemCaloriesBySizeSingle(this.firstItem.Modifiers, this.secondItem.Modifiers, orderTypeId, sizeId);
        const calories = this._localizationService.GetName(caloriesNames) || null;

        const priceRaw = this._getItemPriceForSize(menuItem.Prices, sizeId);
        const price = Util.formatMoney(priceRaw, country);

        return { price, calories };
    }

    /**
     * Calculates total calories for a single-size menu item, considering optional half-half combinations.
     *
     * @param {IOrderItemModifier[]} modifiers - List of modifiers applied to the primary (whole) item.
     * @param {IOrderItemModifier[]} modifiers2 - List of modifiers applied to the second half (if applicable).
     * @param {number} orderTypeId - The ID representing the current order type (e.g., dine-in, delivery).
     * @param {number} sizeId - The ID representing the selected item size.
     * @returns {IName[]} A localized list of calorie information for the configured item.
     */
    public calculateItemCaloriesBySizeSingle(modifiers: IOrderItemModifier[], modifiers2: IOrderItemModifier[], orderTypeId: number, sizeId: number): IName[] {
        const menuItem = this.getMenuItem(HalfModifierSelection.WHOLE);
        const secondHalfModifiers = this.isHalfHalf() ? modifiers2 : null;

        return OnlineOrderingUtil.getItemCaloriesNew(
            this._localizationService,
            menuItem,
            modifiers,
            secondHalfModifiers,
            orderTypeId,
            sizeId,
            this._item.defaultPreModifierId,
            this._data.getProperty("Settings").DSZID,
            this._context.getProperty("languageCode")
        );
    }

    /**
     * Calculates total calories for a menu item with multiple size or half selections.
     *
     * @param {IOrderItemModifier[]} modifiers - List of modifiers applied to the specified half or portion.
     * @param {HalfModifierSelection} halfId - Indicates which half or portion of the item to calculate (e.g., left, right, whole).
     * @param {number} orderTypeId - The ID representing the current order type (e.g., dine-in, pickup).
     * @param {number} sizeId - The ID representing the selected item size.
     * @returns {IName[]} A localized list of calorie information for the given size and half selection.
     */
    public calculateItemCaloriesBySizeMulti(modifiers: IOrderItemModifier[], halfId: HalfModifierSelection, orderTypeId: number, sizeId: number): IName[] {
        const menuItem = this.getMenuItem(halfId);

        return OnlineOrderingUtil.getItemCaloriesNew(
            this._localizationService,
            menuItem,
            modifiers,
            [],
            orderTypeId,
            sizeId,
            this._item.defaultPreModifierId,
            this._data.getProperty("Settings").DSZID,
            this._context.getProperty("languageCode")
        );
    }

    /**
     * Gets the price and calorie information for a specific modifier within a display group.
     *
     * @param {number} modifierId - The ID of the modifier.
     * @param {number | null} displayGroupId - The display group ID that contains the modifier, or null if none.
     * @returns {{ price: string, calories: string | null }} An object containing the formatted price and calories.
     */
    public getModifierPriceAndCalories(modifierId: number, displayGroupId: number | null): { price: string, calories: string | null } {
        const menuItem = this.getMenuItem(HalfModifierSelection.WHOLE);

        const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === modifierId);
        const modifierDisplayGroup = menuItem.ModifierDisplayGroups.find(x => x.ModifierDisplayGroupId === displayGroupId);
        const itemModifier = modifierDisplayGroup?.Modifiers.find(x => x.ModifierId === modifierId);

        if (!modifierData || !modifierDisplayGroup || !itemModifier)
            return { price: "", calories: null };

        const calories = this._getModifierCalories(menuItem, itemModifier, modifierData);
        const price = this._getModifierPriceDisplayText(menuItem, modifierId, modifierDisplayGroup.ModifierDisplayGroupId);

        return { price, calories };
    }

    /**
     * Ensures all modifiers are set to whole item if the item cannot be split half-half.
     *
     * @param {boolean} isHalfable - Whether the item supports half-half configuration.
     */
    public normalizeHalfHalfState(isHalfable: boolean): void {
        if (isHalfable)
            return;

        const modifiers = this._getAllModifiers();
        for (const modifier of modifiers) {
            modifier.halfId = HalfModifierSelection.WHOLE;
        }
    }

    /**
     * Gets the allowed sizes for the current item, taking into account meal deals and mix-and-match rules.
     *
     * @returns {number[]} A list of allowed size IDs for the item.
     */
    public getValidItemSizes(): number[] {
        if (this.mealDeal?.coupon?.Items) {
            const menuItem = this.getMenuItem(HalfModifierSelection.WHOLE);
            const group = this.mealDeal.isMixAndMatch ? 1 : this.mealDeal.selectedGroup;
            return this.mealDeal.coupon?.Items
                .filter(x => x.GroupIndex == group && x.ItemId == menuItem.ItemId)
                .map(x => x.SizeId) ?? [];
        }

        return [];
    }

    public getMergedModifierDisplayGroups(left: IDataItem, right: IDataItem): IMergedModifierGroup[] {
        const modifierDisplayGroups = this._data.getProperty("ModifierDisplayGroups");
        const results: IMergedModifierGroup[] = [];

        const leftGroups = left.ModifierDisplayGroups ?? [];
        const rightGroups = right.ModifierDisplayGroups ?? [];

        const leftMap = new Map(leftGroups.map(g => [g.ModifierDisplayGroupId, g]));
        const rightMap = new Map(rightGroups.map(g => [g.ModifierDisplayGroupId, g]));

        for (const displayGroup of modifierDisplayGroups) {
            if (!displayGroup.IsVisible || displayGroup.MaxSelectionCount <= 1 || displayGroup.MustToggle)
                continue;

            const groupId = displayGroup.ModifierDisplayGroupId;

            const leftGroup = leftMap.get(groupId);
            const rightGroup = rightMap.get(groupId);

            // If neither side has this group, skip
            if (!leftGroup && !rightGroup)
                continue;

            const mergedModifiers = this.mergeModifiers(leftGroup?.Modifiers ?? [], rightGroup?.Modifiers ?? []);

            results.push({
                modifierDisplayGroup: displayGroup,
                modifiers: mergedModifiers
            });
        }

        return results;
    }

    public mergeModifiers(
        leftMods: IItemModifierDisplayGroupModifier[],
        rightMods: IItemModifierDisplayGroupModifier[]
    ): IMergedModifier[] {
        const merged: IMergedModifier[] = [];

        const leftMap = new Map(leftMods.map(m => [m.ModifierId, m]));
        const rightMap = new Map(rightMods.map(m => [m.ModifierId, m]));

        // Union of modifierIds from both halves
        const allIds = new Set<number>([
            ...leftMap.keys(),
            ...rightMap.keys()
        ]);

        for (const id of allIds) {
            const left = leftMap.get(id) ?? null;
            const right = rightMap.get(id) ?? null;

            merged.push({
                leftModifier: left,
                rightModifier: right
            });
        }

        return merged;
    }

    /**
     * Returns modifier display groups common to all given menu items.
     * @param {IDataItem[]} menuItems - Menu items to compare.
     * @returns {any[]} Common modifier display groups.
     */
    public getCommonNonHalfableModifiers(leftItem: IDataItem, rightItem: IDataItem):
        Map<number, {
            left: IItemModifierDisplayGroupModifier,
            right: IItemModifierDisplayGroupModifier
        }[]>
    {
        const allDisplayGroups = this._data.getProperty("ModifierDisplayGroups") ?? [];
        const result = new Map<number, { left: IItemModifierDisplayGroupModifier, right: IItemModifierDisplayGroupModifier }[]>();

        const leftGroups = leftItem.ModifierDisplayGroups ?? [];
        const rightGroups = rightItem.ModifierDisplayGroups ?? [];

        const rightGroupMap = new Map(
            rightGroups.map(g => [g.ModifierDisplayGroupId, g])
        );

        for (const leftGroup of leftGroups) {
            const rightGroup = rightGroupMap.get(leftGroup.ModifierDisplayGroupId);
            if (!rightGroup) continue;

            const displayGroupMeta = allDisplayGroups.find(
                g => g.ModifierDisplayGroupId === leftGroup.ModifierDisplayGroupId
            );

            if (displayGroupMeta?.IsHalfable)
                continue;

            const rightModifiersById = new Map(
                rightGroup.Modifiers.map(m => [m.ModifierId, m])
            );

            const pairs = leftGroup.Modifiers
                .map(leftMod => {
                    const rightMod = rightModifiersById.get(leftMod.ModifierId);
                    if (!rightMod) return null;

                    return { left: leftMod, right: rightMod };
                })
                .filter(Boolean) as { left: IItemModifierDisplayGroupModifier; right: IItemModifierDisplayGroupModifier }[];

            if (pairs.length > 0) {
                result.set(leftGroup.ModifierDisplayGroupId, pairs);
            }
        }

        return result;
    }

    public groupMergedModifiersByWebCategory(modifiers: IMergedModifier[]): Record<number, IMergedModifier[]> {
        return modifiers.reduce((acc, merged) => {
            // Determine category from whichever modifier exists
            const webCatId =
                merged.leftModifier?.ModifierWebCategoryId ??
                merged.rightModifier?.ModifierWebCategoryId;

            // If neither side has a modifier (should not happen), skip
            if (webCatId == null)
                return acc;

            if (!acc[webCatId])
                acc[webCatId] = [];

            acc[webCatId].push(merged);
            return acc;

        }, {} as Record<number, IMergedModifier[]>);
    }

    /**
     * Updates the selected item modifier by applying, removing, or adding a new modifier.
     *
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @param {number} modifierId - The ID of the modifier being altered.
     * @param {number} preModifierId - The pre-modifier ID associated with the modifier.
     * @param {HalfModifierSelection} halfId - The half of the item the modifier applies to.
     * @param {boolean} ignoreSize - Whether to skip size validation for the modifier.
     * @param {boolean} [newModifier=true] - Whether this is a new modifier.
     * @param {boolean} [removeModifier=false] - Whether to remove the modifier instead of adding/updating.
     * @returns {boolean} True if the modifier update was successful, false otherwise.
     */
    public updateSelectedItemModifier(
        modifierDisplayGroupId: number,
        modifierId: number,
        preModifierId: number,
        halfId: HalfModifierSelection,
        ignoreSize: boolean,
        newModifier: boolean = true,
        removeModifier: boolean = false
    ): boolean {
        const modifierDisplayGroup = this.getModifierDisplayGroupById(modifierDisplayGroupId);
        if (!modifierDisplayGroup)
            return false;

        if (!this._checkModifierLimits(modifierDisplayGroupId, preModifierId, newModifier))
            return false;

        const targetHalves =
            halfId === HalfModifierSelection.WHOLE
                ? [HalfModifierSelection.LEFT, HalfModifierSelection.RIGHT]
                : [halfId];

        const resolved = targetHalves.map(h =>
            this._getItemModifier(h, modifierDisplayGroupId, modifierId)
        );

        if (resolved.some(r => r === null))
            return false;

        // Size validation only needs to run once (use LEFT as source of truth)
        if (!ignoreSize) {
            const { menuItem, itemModifier } = resolved[0]!;
            if (!this._validateModifierSize(menuItem, modifierDisplayGroupId, itemModifier))
                return false;
        }

        for (const [index, half] of targetHalves.entries()) {
            const { itemModifier } = resolved[index]!;

            const adjusted = this._applyModifierUpdate(
                modifierId,
                itemModifier,
                modifierDisplayGroup,
                preModifierId,
                half,
                removeModifier
            );

            if (!adjusted && preModifierId !== this._item.nonePreModifierId) {
                this._addNewModifier(
                    modifierDisplayGroup,
                    itemModifier,
                    modifierId,
                    preModifierId,
                    half
                );
            }
        }

        // Validate Activate/Deactivate Max Modifiers
        if (!this._validateFinalItemPrice(modifierId))
            return false;

        this.recalculateItemPricing();
        return true;
    }

    private _getItemModifier(
        halfId: HalfModifierSelection,
        modifierDisplayGroupId: number,
        modifierId: number
    ): {
        menuItem: IDataItem;
        itemModifier: IItemModifierDisplayGroupModifier;
    } | null {
        const menuItem = this.getMenuItem(halfId);
        const group = menuItem.ModifierDisplayGroups.find(g => g.ModifierDisplayGroupId === modifierDisplayGroupId);
        if (!group)
            return null;

        const modifier = group.Modifiers.find(m => m.ModifierId === modifierId);
        if (!modifier)
            return null;

        return { menuItem, itemModifier: modifier };
    }

    /**
     * Attempts to construct order items (single or half-half) from the current configuration.
     *
     * @returns {{ items: { oItem1: IOrderItem, oItem2: IOrderItem | null } | null, message?: string }}
     * Returns the created order items if valid, or null with an error message if invalid.
     */
    public createOrderItemsIfValid(): { items: { oItem1: IOrderItem, oItem2: IOrderItem | null } | null, message?: string } {
        if (this._item.sizeId === -1)
            return { items: null, message: this._localizationService.names("Error_SelectSize") };

        const isHalfHalf = this.isHalfHalf();
        const halfIndex1 = isHalfHalf ? 1 : 0;
        const halfIndex2 = isHalfHalf ? 2 : 0;

        const firstItem = this.firstItem;
        const secondItem = this.secondItem;

        let orderItem1: IOrderItem;
        let orderItem2: IOrderItem | null;
        try {
            orderItem1 = this._order._createOrderItem(this.getMenuItem(HalfModifierSelection.LEFT), this.sizeId, halfIndex1, this.quantity, firstItem.Modifiers, firstItem.BeforeTaxDiscount, firstItem.AfterTaxDiscount);
            orderItem2 = isHalfHalf && this.getMenuItem(HalfModifierSelection.RIGHT)
                ? this._order._createOrderItem(this.getMenuItem(HalfModifierSelection.RIGHT), this.sizeId, halfIndex2, this.quantity, secondItem.Modifiers, secondItem.BeforeTaxDiscount, secondItem.AfterTaxDiscount)
                : null;
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;

            return { items: null, message: this._localizationService.names("ITEM_PRICING_UNAVAILABLE") };
        }

        const displayGroups = this._data.getProperty("ModifierDisplayGroups");
        const isValidated = this._validateItemConfiguration(orderItem1, orderItem2, displayGroups);

        return isValidated.success
            ? { items: { oItem1: orderItem1, oItem2: orderItem2 } }
            : { items: null, message: isValidated.message };
    }

    /**
     * Builds and finalizes the order items for checkout, handling editing state,
     * coupons, and half-half items.
     *
     * @returns {Promise<number>} The final item key for the order.
     */
    public async finalizeOrderItemsAsync(): Promise<number> {
        if (this.isEditing() && !this.mealDeal)
            this._item.removedCoupons = await this._handleEditingCouponsAsync();

        const isHalfHalf = this.isHalfHalf();
        const itemKey = this._determineItemKey();
        const halfIndex = isHalfHalf ? 1 : 0;

        let item: IOrderItem;
        let item2: IOrderItem | undefined;
        try {
            item = this._prepareOrderItem(this.firstItem, itemKey, halfIndex);
            if (isHalfHalf)
                item2 = this._prepareOrderItem(this.secondItem, itemKey, 2, item.ItemRecId);
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;

            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names("ITEM_PRICING_UNAVAILABLE"), true);
            return -1;
        }

        const added = await this._finalizeOrderItems(item, item2);
        if (!added)
            return -1;

        if (this.isEditing() && !this.mealDeal)
            await this._handlePostEditingAdjustmentsAsync(item, this._item.removedCoupons);

        return itemKey;
    }

    /**
     * Handles automatic coupon application and loyalty sync.
     * UI (favorites, dataLayer) should handle separately.
     */
    public async runPostOrderLogicAsync(): Promise<void> {
        if (!this.mealDeal) {
            const couponIdsToSkip: number[] = [];
            if(this.isEditing()){
                // Bug 77670: skip coupons already removed and attempted to reapply in _handlePostEditingAdjustmentsAsync when editing an item
                this._item.removedCoupons.forEach(x => couponIdsToSkip.push(x.couponId));
            } else {
                //Bug 77617: skip percent off entire order coupon type which is already attempted to reapply in order.addItems
                const appliedPercentOffOrderCoupons = this._order.getAppliedCouponsByType([CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER]);
                appliedPercentOffOrderCoupons.forEach((coupon, key) => couponIdsToSkip.push(coupon.dataCoupon.CouponId));
            }
                
            await this._order.autoApplyCouponsAsync(this._profileService.loyaltyProvider, couponIdsToSkip);
        }

        await this._profileService.loyaltyProvider.batchComparison(true);
    }

    private _eventBusListeners(): void {
        this._eventBus.on(EventTypes.CUSTOMIZE_ITEM_SIZE_CHANGE, this.setSizeId);
        this._eventBus.on(EventTypes.STORE_CHANGED, this._updatedPreModifierIds);
    }

    /**
     * Creates the customization model for the item.
     * 
     * @param sizeId - The size ID of the item.
     * @param quantity - The quantity of the item.
     * @param itemsToEdit - The list of items to edit.
     * @returns {ICustomizeItem} The customized item model.
     */
    private _createModel(
        item: IDataItem,
        sizeId: number,
        quantity: number,
        itemsToEdit: IOrderItem[],
        mealDeal: IDealBuilderCustomizeItem | null,
        recentOrder: boolean,
        elementIndex?: number
    ): ICustomizeItemCore {
        const state: ICustomizeItemCore = {
            sizeId,
            quantity: Math.max(quantity, 1),
            itemsToEdit,
            baseItem: this._createBaseItem(),
            baseItem2: this._createBaseItem(),
            instructions: [],
            comments: "",
            mealDeal,
            recentOrder,
            defaultPreModifierId: this._data.getProperty("DefaultPreModifierId"),
            nonePreModifierId: this._data.getProperty("NonePreModifierId"),
            elementIndex: elementIndex ?? 0,
            isHalfMode: false,
            itemState: new Map([
                [
                    HalfModifierSelection.LEFT,
                    {
                        item: item,
                        modifierGroups: new Map<number | null, CustomizeItemModifierGroup>(),
                    },
                ],
                [
                    HalfModifierSelection.RIGHT,
                    {
                        item: item,
                        modifierGroups: new Map<number | null, CustomizeItemModifierGroup>(),
                    },
                ],
            ]),
            removedCoupons: []
        };

        return state;
    }

    private readonly _updatedPreModifierIds = (): void => {
        this._item.defaultPreModifierId = this._data.getProperty("DefaultPreModifierId");
        this._item.nonePreModifierId = this._data.getProperty("NonePreModifierId");
    }

    /**
     * Creates a base customization item with default values.
     * 
     * @returns {ICustomizeItemBaseItem} The base customization item.
     */
    private _createBaseItem(): ICustomizeItemBaseItem {
        return {
            ItemId: 0,
            Modifiers: [],
            PriceIncludesTax: false,
            Price: 0.0,
            MinPrice: 0.0,
            HalfHalfPrice: 0.0,
            MenuPrice: 0.0,
            MenuPriceTax: 0.0,
            Tax: 0.0,
            TaxAmount: 0.0,
            Taxes: [],
            AfterTaxDiscount: 0,
            BeforeTaxDiscount: 0
        };
    }

    /**
     * Retrieves the current items for customization.
     * 
     * @returns {ICustomizeItemBaseItem[]} Array of items for customization.
     */
    private _getItems(): { item: ICustomizeItemBaseItem, halfId: HalfModifierSelection }[] {
        return this.isHalfHalf() ? [
            { item: this.firstItem, halfId: HalfModifierSelection.LEFT },
            { item: this.secondItem, halfId: HalfModifierSelection.RIGHT }
        ] : [{ item: this.firstItem, halfId: HalfModifierSelection.WHOLE }];
    }

    /**
     * Retrieves a menu item from the global data store by its unique identifier.
     *
     * @private
     * @param {number} itemId - The unique identifier of the menu item to retrieve.
     * @returns {IDataItem | undefined} The matching menu item if found; otherwise, `undefined`.
     */
    private _getMenuItemById(itemId: number): IDataItem | undefined {
        return this._data.getProperty("Items").find(x => x.ItemId === itemId);
    }

    /**
     * Retrieves the half customization state (left or right) for the current item.
     * Throws an error if the requested half has not been initialized in `itemState`.
     *
     * @private
     * @param {HalfModifierSelection} selection - Which half to retrieve (`LEFT` or `RIGHT`).
     * @returns {CustomizeItemHalfCustomization} The half customization data.
     * @throws {Error} If the specified half has not been initialized.
     */
    private _getHalf(selection: HalfModifierSelection): CustomizeItemHalfCustomization {
        const half = this._item.itemState.get(selection);
        if (!half)
            throw new Error(`HalfModifierSelection ${selection} is not initialized`);

        return half;
    }

    /**
     * Retrieves all modifier instances across both halves (left and right) of the current item.
     * Iterates through all modifier groups within each half and flattens them into a single array.
     *
     * @private
     * @returns {CustomizeItemModifier[]} An array of all modifiers applied to both halves.
     */
    private _getAllModifiers(): CustomizeItemModifier[] {
        const left = this._getAllModifiersByHalf(HalfModifierSelection.LEFT);
        const right = this._getAllModifiersByHalf(HalfModifierSelection.RIGHT);

        return [...left, ...right];
    }

    private _getAllModifiersByHalf(halfId: HalfModifierSelection): CustomizeItemModifier[] {
        const side = this._getHalf(halfId);

        const result: CustomizeItemModifier[] = [];
        for (const group of side.modifierGroups.values()) {
            for (const mod of group.modifiers.values())
                result.push(mod);
        }

        return result;
    }

    /**
     * Determines whether two customizable items are equivalent
     * based on item ID and modifier composition (as a multiset of ModifierId + PreModifierId pairs).
     *
     * @private
     * @param {ICustomizeItemBaseItem} item1 - The first item to compare.
     * @param {ICustomizeItemBaseItem} item2 - The second item to compare.
     * @returns {boolean} True if both items are equal; otherwise false.
     */
    private _areItemsEqual(item1: ICustomizeItemBaseItem, item2: ICustomizeItemBaseItem): boolean {
        // 1) Compare ItemId
        if (item1.ItemId !== item2.ItemId)
            return false;

        // 2) Compare modifier length
        if (item1.Modifiers.length !== item2.Modifiers.length)
            return false;

        // 3) Compare as a multiset of (ModifierId, PreModifierId) pairs
        const key = (m: IOrderItemModifier) => `${m.ModifierId}|${m.PreModifierId ?? ''}`;
        const counts = new Map<string, number>();

        for (const m of item1.Modifiers) {
            const k = key(m);
            counts.set(k, (counts.get(k) ?? 0) + 1);
        }

        for (const m of item2.Modifiers) {
            const k = key(m);
            const count = counts.get(k);
            if (!count) return false;           // missing or exhausted in bag
            count === 1 ? counts.delete(k) : counts.set(k, count - 1);
        }

        return counts.size === 0;
    }

    /**
     * Retrieves modifiers for the specified half of the item, combining them with whole modifiers.
     * @param halfId - The half (LEFT, RIGHT, or WHOLE) to retrieve modifiers for.
     * @returns {IOrderItemModifier[]} An array of modifiers applicable to the specified half.
     */
    private _getModifiersByHalf(halfId: HalfModifierSelection): IOrderItemModifier[] {
        if (halfId === HalfModifierSelection.WHOLE)
            return [];

        const side = this._getHalf(halfId);
        const selectedModifiers: CustomizeItemModifier[] = [];

        for (const group of side.modifierGroups.values()) {
            // Get all modifiers in the group
            const modifiers = Array.from(group.modifiers.values());

            if (this.isSingleChoiceGroup(group.displayGroup)) {
                // Find the selected one, or if none, the default one
                const chosen = modifiers.find(m => m.isSelected && m.modifier !== null) ?? modifiers.find(m => m.isDefault && m.modifier !== null);

                if (chosen)
                    selectedModifiers.push(chosen);
            } else {
                const chosen = modifiers.filter(m => (m.isSelected || m.isDefault) && m.modifier !== null);
                selectedModifiers.push(...chosen);
            }
        }

        return selectedModifiers.map(x => x.modifier) as IOrderItemModifier[];
    }

    /**
     * Adds a new modifier to the order with the specified properties.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The display group data.
     * @param {IItemModifierDisplayGroupModifier} itemModifier - The item modifier data.
     * @param {number} modifierId - The ID of the modifier.
     * @param {number} preModifierId - The ID of the pre-modifier.
     * @param {HalfModifierSelection} halfId - The half selection.
     */
    private _addNewModifier(modifierDisplayGroup: IDataModifierDisplayGroup, itemModifier: IItemModifierDisplayGroupModifier, modifierId: number, preModifierId: number, halfId: HalfModifierSelection): void {
        const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === modifierId);

        const newModifier: IOrderItemModifier = {
            ModifierId: modifierId,
            Name: itemModifier.Name,
            Index: itemModifier.Index,
            ModifierDisplayGroupIndex: 0, //itemModifierDisplayGroup.Index,
            iIndex: 0, //iModIDX,
            iModifierDisplayGroupIndex: 0, //iDGIDX,
            PreModifierId: preModifierId,
            IsDefault: itemModifier.IsDefault,
            ModifierDisplayGroupId: modifierDisplayGroup.ModifierDisplayGroupId,
            MergedModifierDisplayGroupId: itemModifier.MergedModifierDisplayGroupId,
            CountForPricing: itemModifier.CountForPricing,
            CountForPricingByItself: itemModifier.CountForPricingByItself,
            Price: 0,
            IsCrust: modifierData?.IsCrust ?? false
        };

        this._updateModifier({ halfId, modifierId, preModifierId, isSelected: true, modifier: newModifier });
    }

    /**
     * Retrieves modifier group data for a given half and display group.
     *
     * @private
     * @param {HalfModifierSelection} halfId - The half of the item (LEFT, RIGHT, or WHOLE).
     * @param {number} displayGroupId - The ID of the modifier display group.
     * @returns {CustomizeItemModifierGroup | undefined} The modifier group data, or undefined if not found.
     */
    private _getModifierDisplayGroupData(halfId: HalfModifierSelection, displayGroupId: number): CustomizeItemModifierGroup | undefined {
        const effectiveHalf = halfId === HalfModifierSelection.WHOLE ? HalfModifierSelection.LEFT : halfId;
        const halfData = this._item.itemState.get(effectiveHalf);
        return halfData?.modifierGroups.get(displayGroupId);
    }

    /**
     * Retrieves modifier data (left/right halves and whether whole) for a given modifier ID.
     *
     * @private
     * @param {number} modifierId - The ID of the modifier to locate.
     * @returns {CustomizeItemModifierResult} Object containing modifier presence, half info, and instances.
     */
    private _getModifierData(modifierId: number): CustomizeItemModifierResult {
        let left: CustomizeItemModifier | null = null;
        let right: CustomizeItemModifier | null = null;

        for (const half of [HalfModifierSelection.LEFT, HalfModifierSelection.RIGHT]) {
            const halfData = this._getHalf(half);
            if (!halfData) continue;

            for (const group of halfData.modifierGroups.values()) {
                const modifier = group.modifiers.get(modifierId);
                if (modifier) {
                    if (half === HalfModifierSelection.LEFT)
                        left = modifier;
                    else
                        right = modifier;

                    break;
                }
            }
        }

        const hasModifier = Boolean(left || right);

        const isWhole =
            left?.halfId === HalfModifierSelection.WHOLE ||
            right?.halfId === HalfModifierSelection.WHOLE;

        return { hasModifier, isWhole, left, right };
    }

    /**
     * Updates modifier attributes for one or both halves based on the given update data.
     *
     * @private
     * @param {{
     *   modifierId: number,
     *   halfId: HalfModifierSelection,
     *   preModifierId?: number,
     *   isSelected?: boolean,
     *   isDefault?: boolean,
     *   modifier?: IOrderItemModifier | null
     * }} data - Data describing which modifier to update and what fields to change.
     */
    private _updateModifier(data: {
        modifierId: number,
        halfId: HalfModifierSelection,
        preModifierId?: number,
        isSelected?: boolean,
        isDefault?: boolean,
        modifier?: IOrderItemModifier | null
    }): void {
        const { modifierId, halfId, preModifierId, isSelected, isDefault, modifier } = data;
        const { left, right, hasModifier } = this._getModifierData(modifierId);

        if (!hasModifier)
            return;

        const halves: ReadonlyArray<[HalfModifierSelection, CustomizeItemModifier | null]> = [
            [HalfModifierSelection.LEFT, left],
            [HalfModifierSelection.RIGHT, right],
        ];

        // Normalize both halves halfId
        for (const [side, halfObj] of halves) {
            if (!halfObj)
                continue;

            halfObj.halfId = (halfId === HalfModifierSelection.WHOLE)
                ? HalfModifierSelection.WHOLE
                : side; // LEFT gets LEFT, RIGHT gets RIGHT
        }

        // 2) Apply field updates to the targeted halves
        const shouldUpdate = (side: HalfModifierSelection) =>
            halfId === HalfModifierSelection.WHOLE || halfId === side;

        for (const [side, halfObj] of halves) {
            if (!halfObj || !shouldUpdate(side))
                continue;

            if (isDefault !== undefined)
                halfObj.isDefault = isDefault;

            if (preModifierId !== undefined)
                halfObj.preModifierId = preModifierId;

            if (isSelected !== undefined)
                halfObj.isSelected = isSelected;

            if (modifier !== undefined)
                halfObj.modifier = modifier;
        }

        // 3) Check if left and right should collapse to WHOLE
        if (left && right && this._shouldCollapseToWhole(left, right))
            left.halfId = right.halfId = HalfModifierSelection.WHOLE;

        // 4) Update All PremodifierIds
        this._updateModifiersPreModifiers();
    }

    /**
     * Checks whether the modifier meets the selection and group constraints.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @param {number} preModifierId - The ID of the pre-modifier.
     * @param {boolean} newModifier - Whether the modifier is new.
     * @returns {boolean} True if the modifier is valid, false otherwise.
     */
    private _checkModifierLimits(modifierDisplayGroupId: number, preModifierId: number, newModifier: boolean): boolean {
        if (!newModifier)
            return true;

        if (!this._checkDisplayGroupMax(modifierDisplayGroupId) || !this._checkModifiersMax(modifierDisplayGroupId))
            return false;

        return true;
    }

    /**
     * Checks if the total modifiers for a modifier display group exceed the maximum allowed.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @returns {boolean} True if within the limit, false otherwise.
     */
    private _checkModifiersMax(modifierDisplayGroupId: number): boolean {
        const modifierDisplayGroup = this.getModifierDisplayGroupById(modifierDisplayGroupId);
        if (!modifierDisplayGroup)
            return true;

        if (modifierDisplayGroup.MaxSelectionCount == 1 || modifierDisplayGroup.MustToggle || !modifierDisplayGroup.IsVisible)
            return true;

        const oItems = this._getItems();
        for (const oItem of oItems) {
            const menuItem = this.getMenuItem(oItem.halfId);

            const mCount = oItem.item.Modifiers.reduce((count, mod) => {
                if (!mod.IsDefault) {
                    const dGroup = this._getModifierDisplayGroup(menuItem, mod.ModifierId);
                    if (dGroup && !dGroup.MustToggle && dGroup.MaxSelectionCount !== 1)
                        count++;
                }
                return count;
            }, 0);

            if (mCount >= menuItem.MaximumModifierCount) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names(
                        "Error_ReachedMaxNumberOfModifiersOnItem"
                    ),
                    true
                );
                return false;
            }
        }

        return true;
    }

    /**
     * Retrieves the modifier display group for a given modifier ID.
     * @param {number} modifierId - The ID of the modifier.
     * @returns {IDataModifierDisplayGroup | null} The modifier display group or null if not found.
     */
    private _getModifierDisplayGroup(menuItem: IDataItem, modifierId: number): IDataModifierDisplayGroup | null {
        const modifierDisplayGroups = this._data.getProperty("ModifierDisplayGroups");
        for (const modifierDisplayGroup of menuItem.ModifierDisplayGroups) {
            const modifier = modifierDisplayGroup.Modifiers.find(m => m.ModifierId === modifierId);
            if (modifier)
                return modifierDisplayGroups.find(x => x.ModifierDisplayGroupId === modifierDisplayGroup.ModifierDisplayGroupId) || null;
        }

        return null;
    }

    /**
     * Validates the size constraints for the modifier and handles errors if invalid.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @param {number} modifierId - The ID of the modifier.
     * @param {number} preModifierId - The ID of the pre-modifier.
     * @param {HalfModifierSelection} halfId - The half selection.
     * @param {IItemModifierDisplayGroupModifier} itemModifier - The item modifier data.
     * @param {boolean} newModifier - Whether the modifier is new.
     * @returns {boolean} True if the size is valid, false otherwise.
     */
    private _validateModifierSize(menuItem: IDataItem, modifierDisplayGroupId: number, itemModifier: IItemModifierDisplayGroupModifier): boolean {
        if (OnlineOrderingUtil.isModOfferedOnSize(itemModifier, this._item.sizeId))
            return true;

        // Handle invalid size logic, showing a dialog.
        this._handleInvalidModifierSize(menuItem, itemModifier, modifierDisplayGroupId);
        return false;
    }

    /**
     * Handles the case when a modifier is not valid for the selected size.
     * Displays an error message and adjusts the size if possible.
     * @param {IItemModifierDisplayGroupModifier} miMod - The invalid modifier.
     * @param {IItemOrderTypeSize | null} itemSize - The size details, if available.
     */
    private _handleInvalidModifierSize(menuItem: IDataItem, miMod: IItemModifierDisplayGroupModifier, modifierDisplayGroupId: number): void {
        const modifier = this._data.getProperty("Modifiers").find(x => x.ModifierId === miMod.ModifierId);
        const modName = modifier ? this._localizationService.GetName(modifier.Names) : "";
        const sizes = this._getAddOnPricesForSizes(menuItem, miMod);
        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayer,
            this._localizationService.names("Error_ModifierIsNotBeingOfferedOnSize")
                .replaceAll("{{modifier}}", modName)
                .replaceAll("{{sizes}}", sizes),
            true
        );

        setTimeout(() => {
            this._revertToFirstModifier(modifierDisplayGroupId);
        }, 100);
    }

    /**
     * Reverts to the first modifier in the specified modifier display group.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     */
    private _revertToFirstModifier(modifierDisplayGroupId: number): void {
        const firstModifier = this.getFirstModifierByModifierDisplayGroup(HalfModifierSelection.WHOLE, modifierDisplayGroupId);
        if (firstModifier)
            document.getElementById(`rdo_customize_item_modifier_display_group_${modifierDisplayGroupId}_${firstModifier.modifierId}`)?.click();
    }

    /**
     * Retrieves a modifier display group by its ID.
     * @param {number | null} modifierDisplayGroupId - The ID of the modifier display group.
     * @returns {IDataModifierDisplayGroup | null} The modifier display group or null if not found.
     */
    public getModifierDisplayGroupById(modifierDisplayGroupId: number | null): IDataModifierDisplayGroup | null {
        const modifierDisplayGroups = this._data.getProperty("ModifierDisplayGroups");
        const modifierDisplayGroup = modifierDisplayGroups.find(x => x.ModifierDisplayGroupId === modifierDisplayGroupId);
        return modifierDisplayGroup ?? null;
    }

    /**
     * Updates the selected modifier, applying constraints for toggling and group rules.
     * @param {IItemModifierDisplayGroupModifier} itemModifier - The item modifier data.
     * @param {IDataModifierDisplayGroup} modifierDisplayGroup - The display group data.
     * @param {number} modifierId - The ID of the modifier.
     * @param {number} preModifierId - The ID of the pre-modifier.
     * @param {HalfModifierSelection} halfId - The half selection.
     * @returns {boolean} True if the modifier was successfully updated, false otherwise.
     */
    private _updateSelectedModifier(
        itemModifier: IItemModifierDisplayGroupModifier,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        modifierId: number,
        preModifierId: number,
        halfId: HalfModifierSelection,
        removeModifier: boolean = false
    ): boolean {
        // If single select modifier
        if (this.isSingleChoiceGroup(modifierDisplayGroup)) {
            const modifier = this.getFirstModifierByModifierDisplayGroup(halfId, modifierDisplayGroup.ModifierDisplayGroupId);
            if (!modifier)
                return false;

            let result = false;
            if (modifier.modifierId === modifierId) {
                if (removeModifier)
                    this._updateModifier({ halfId, modifierId: modifier.modifierId, preModifierId: this._item.nonePreModifierId, isSelected: false });
                else
                    this._updateModifier({ halfId, modifierId: modifier.modifierId, preModifierId, isSelected: true });

                result = true;
            } else {
                this._updateModifier({ halfId, modifierId: modifier.modifierId, preModifierId: this._item.nonePreModifierId, isSelected: false });
            }

            this._updateModifiersPreModifiers();
            return result;
        }

        // If multi select option
        const oItem = halfId === HalfModifierSelection.RIGHT ? this.secondItem : this.firstItem;
        const modifier = oItem.Modifiers.find(x => x.ModifierId === modifierId);
        if (!modifier)
            return false;

        const nonePreModifierId = this._item.nonePreModifierId;

        const shouldDeselect =
            (itemModifier.IsDefault && (preModifierId === nonePreModifierId || removeModifier)) ||
            (!itemModifier.IsDefault && preModifierId === nonePreModifierId);

        const selectPremodifierId = (itemModifier.IsDefault && (preModifierId === nonePreModifierId || removeModifier))
            ? nonePreModifierId
            : preModifierId;

        this._updateModifier({
            halfId,
            modifierId,
            preModifierId: selectPremodifierId,
            isSelected: !shouldDeselect
        });

        this._updateModifiersPreModifiers();
        return true;
    }

    /**
     * Retrieves the first selected modifier for a given modifier display group.
     * @param {number} ModifierDisplayGroupId - The ID of the modifier display group.
     * @returns {CustomizeItemModifier | null} The first selected modifier or null if none is selected.
     */
    public getFirstModifierByModifierDisplayGroup(halfId: HalfModifierSelection, modifierDisplayGroupId: number): CustomizeItemModifier | null {
        let modifierDisplayGroup = this._getModifierDisplayGroupData(halfId, modifierDisplayGroupId);
        if (!modifierDisplayGroup || modifierDisplayGroup.modifiers.size === 0)
            return null;

        const modifier = [...modifierDisplayGroup.modifiers.values()].find(x => x.isSelected);
        if (modifier)
            return modifier;

        if (![HalfModifierSelection.WHOLE, HalfModifierSelection.LEFT].includes(halfId))
            return null;

        modifierDisplayGroup = this._getModifierDisplayGroupData(HalfModifierSelection.RIGHT, modifierDisplayGroupId);
        if (!modifierDisplayGroup || modifierDisplayGroup.modifiers.size === 0)
            return null;
            
        return [...modifierDisplayGroup.modifiers.values()].find(x => x.isSelected) ?? null;
    }

    /**
     * Updates the pre-modifier IDs for all selected modifiers in the item.
     */
    private _updateModifiersPreModifiers(): void {
        for (const half of [HalfModifierSelection.LEFT, HalfModifierSelection.RIGHT]) {
            const halfData = this._item.itemState.get(half);
            if (!halfData)
                continue;

            for (const [, group] of halfData.modifierGroups) {
                if (group.modifiers.size === 0)
                    continue;

                for (const [id, modifier] of group.modifiers) {
                    if (!modifier.modifier)
                        continue;

                    const updated = {
                        ...modifier,
                        modifier: {
                            ...modifier.modifier,
                            PreModifierId: modifier.preModifierId
                        }
                    };

                    group.modifiers.set(id, updated);
                }
            }
        }
    }

    private _getAddOnPricesForSizes(menuItem: IDataItem, modifier: IItemModifierDisplayGroupModifier): string {
        const validSizes: string[] = [];
        const sizes = OnlineOrderingUtil.GetItemSizes(menuItem, this._order.getProperty("OrderTypeID"));

        for (const size of sizes) {
            const isHalfHalf = this.isHalfHalf();

            if (!isHalfHalf || (isHalfHalf && size.IsHalfable)) {
                const modPrice = OnlineOrderingUtil.GetModifierAddToPrice(modifier, size.SizeId);
                if (modPrice > -1) {
                    console.log(this._data.getProperty("Sizes"));
                    const sizeNames = this._data.getProperty("Sizes").find(x => x.SizeId === size.SizeId)?.Names ?? [];
                    const sizeName = this._localizationService.GetName(sizeNames);
                    validSizes.push(sizeName);
                }
            }
        }

        return validSizes?.join(", ") ?? "";
    }

    /**
     * Finds the largest valid size for a given modifier.
     * @param {IItemModifierDisplayGroupModifier} miMod - The modifier to check.
     * @returns {number | null} The size ID if valid, or null otherwise.
     */
    private _findLargestValidSizeForModifier(menuItem: IDataItem, miMod: IItemModifierDisplayGroupModifier): number | null {
        const itemSizes = OnlineOrderingUtil.GetItemSizes(menuItem, this._order.getProperty("OrderTypeID")).sort((a, b) => b.Index - a.Index);
        return itemSizes.find(size => OnlineOrderingUtil.isModOfferedOnSize(miMod, size.SizeId))?.SizeId ?? null;
    }

    /**
     * Calculates pricing for half-half items.
     */
    private _calculateHalfHalfPricing(): void {
        const highestPrice = this._data.getProperty("Settings").HHP === 1;
        const maxPrice = Math.max(0, this.firstItem.MenuPrice, this.secondItem.MenuPrice);
        const maxPriceTax = Math.max(0, this.firstItem.MenuPriceTax, this.secondItem.MenuPriceTax);
        const totalPrice = this.firstItem.MenuPrice + this.secondItem.MenuPrice;
        const totalPriceTax = this.firstItem.MenuPriceTax + this.secondItem.MenuPriceTax;

        const hhPrice = highestPrice ? Util.Float2(maxPrice) : Util.Float2(totalPrice / 2);
        const hhPriceTax = highestPrice ? Util.Float2(maxPriceTax) : Util.Float2(totalPriceTax / 2);

        this.firstItem.HalfHalfPrice = hhPrice;
        this.secondItem.HalfHalfPrice = hhPrice;

        if (highestPrice) {
            this.firstItem.Price = Util.Float2(hhPrice / 2);
            this.firstItem.Tax = Util.Float2(hhPriceTax / 2);
            this.secondItem.Price = Util.Float2(hhPrice / 2);
            this.secondItem.Tax = Util.Float2(hhPriceTax / 2);
        } else {
            this.firstItem.Price = Util.Float2(this.firstItem.MenuPrice / 2);
            this.firstItem.Tax = Util.Float2(this.firstItem.MenuPriceTax / 2);
            this.secondItem.Price = Util.Float2(this.secondItem.MenuPrice / 2);
            this.secondItem.Tax = Util.Float2(this.secondItem.MenuPriceTax / 2);
        }

        const totalAssignedPrice = this.firstItem.Price + this.secondItem.Price;
        if (totalAssignedPrice !== hhPrice) {
            const diff = Util.Float2(hhPrice - totalAssignedPrice);
            this.secondItem.Price = Util.Float2(this.secondItem.Price + diff);
        }

        this._setTaxOnItem(this.firstItem, HalfModifierSelection.LEFT);
        this._setTaxOnItem(this.secondItem, HalfModifierSelection.RIGHT);
    }

    /**
     * Calculates and sets taxes for the selected item(s).
     */
    private _taxSelectedItem(): void {
        const oItems = this._getItems();
        for (const oItem of oItems) {
            this._setTaxOnItem(oItem.item, oItem.halfId);
        }
    }

    /**
     * Sets tax details for the given item based on its properties.
     * @param {ICustomizeItemBaseItem} item - The item for which tax is being calculated.
     */
    private _setTaxOnItem(item: ICustomizeItemBaseItem, halfId: HalfModifierSelection): void {
        const menuItem = this.getMenuItem(halfId);
        const oItemTax: IGetItemTaxoItem = {
            SizeId: this._item.sizeId,
            Quantity: this._item.quantity,
            PriceIncludesTax: item.PriceIncludesTax,
            Price: item.Price,
            AfterTaxDiscount: item.AfterTaxDiscount,
            BeforeTaxDiscount: item.BeforeTaxDiscount
        };

        const taxData = OnlineOrderingUtil.GetItemTax(menuItem, oItemTax, this._order._getItemTaxDataDependencies());
        item.TaxAmount = taxData.TaxableAmount;
        item.Taxes = taxData.Taxes;
        item.Tax = taxData.TotalTax;
    }

    /**
     * Checks if the maximum selection count for a modifier display group has been exceeded.
     * @param {number} modifierDisplayGroupId - The ID of the modifier display group.
     * @returns {boolean} True if within the limit, false otherwise.
     */
    private _checkDisplayGroupMax(modifierDisplayGroupId: number): boolean {
        const modifierDisplayGroup = this.getModifierDisplayGroupById(modifierDisplayGroupId);
        if (!modifierDisplayGroup)
            return true;

        const oItems = this._getItems();
        for (const oItem of oItems) {
            const modCount = oItem.item.Modifiers.filter(x => x.ModifierDisplayGroupId === modifierDisplayGroup.ModifierDisplayGroupId
                && x.PreModifierId !== this._item.nonePreModifierId).length;

            if (modifierDisplayGroup.MaxSelectionCount > 1
                && !modifierDisplayGroup.MustToggle
                && modifierDisplayGroup.IsVisible
                && modCount >= modifierDisplayGroup.MaxSelectionCount) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names(
                        "Error_ReachedMaxNumberOfModifiersOnItem"
                    ),
                    true
                );
                return false;
            }
        }

        return true;
    }

    /**
     * Checks whether a specific size is valid for half-half configurations.
     * @param {number} sizeId - The ID of the size to validate.
     * @returns {boolean} True if the size is valid and halfable, false otherwise.
     */
    private _checkSizeIsHalfable(menuItem: IDataItem, sizeId: number): boolean {
        const itemSize = OnlineOrderingUtil.GetItemSize(menuItem, this._order.getProperty("OrderTypeID"), sizeId);

        if (!itemSize?.IsHalfable) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names(
                    "Error_HalfNotAvailableForSelectedSize"
                ),
                true
            );
            return false;
        }

        return true;
    }

    /**
     * Checks whether the item modifiers are valid for a given size.
     * If invalid, adjusts the size or displays an error message.
     * @param {IOrderItemModifier[]} itemModifiers - The list of item modifiers to check.
     * @param {number} sizeId - The size ID to validate against.
     * @returns {boolean} True if all modifiers are valid for the size, false otherwise.
     */
    private _checkItemModifiersForSizes(menuItem: IDataItem, itemModifiers: IOrderItemModifier[], sizeId: number): boolean {
        for (const oMod of itemModifiers) {
            const miMdg = menuItem.ModifierDisplayGroups.find(x => x.ModifierDisplayGroupId === oMod.ModifierDisplayGroupId);
            if (!miMdg)
                continue;

            const miMod = miMdg.Modifiers.find(x => x.ModifierId === oMod.ModifierId);
            if (!miMod)
                continue;

            if (!OnlineOrderingUtil.isModOfferedOnSize(miMod, sizeId)) {
                this._handleInvalidModifierSize(menuItem, miMod, oMod.ModifierDisplayGroupId);
                return false;
            }
        }
        return true;
    }

    /**
     * Creates a modifier and adds it to the item's modifiers list if it doesn't already exist.
     * @param halfId - The half of the item the modifier is associated with.
     * @param modifierDisplayGroup - The display group associated with the modifier.
     * @param modifierId - The unique ID of the modifier.
     * @param premodifierId - The premodifier ID, if applicable.
     * @param isSelected - Whether the modifier is selected.
     * @param isDefault - Whether the modifier is a default selection.
     * @param modifier - The modifier object to associate, if any.
     */
    private _createModifier(halfId: HalfModifierSelection, modifierDisplayGroup: IDataModifierDisplayGroup, modifierId: number, preModifierId: number, isSelected: boolean, isDefault: boolean, modifier: IOrderItemModifier | null): void {
        const halves = halfId === HalfModifierSelection.WHOLE ? [HalfModifierSelection.LEFT, HalfModifierSelection.RIGHT] : [halfId];
        for (const half of halves) {
            const halfData = this._getHalf(half);

            let group = halfData.modifierGroups.get(modifierDisplayGroup.ModifierDisplayGroupId);
            if (!group) {
                group = {
                    displayGroup: modifierDisplayGroup,
                    modifiers: new Map<number, CustomizeItemModifier>()
                };
                halfData.modifierGroups.set(modifierDisplayGroup.ModifierDisplayGroupId, group);
            }

            if (!group.modifiers.has(modifierId))
                group.modifiers.set(modifierId, {
                    modifierId,
                    displayGroupId: modifierDisplayGroup.ModifierDisplayGroupId,
                    isDefault,
                    isSelected,
                    preModifierId,
                    halfId,
                    modifier
                });
        }
    }

    /**
     * Retrieve editable modifier information for half/whole items
     * based on a modifier group and modifier ID. Used when an item
     * can have modifiers applied to the left, right, or whole selection.
     *
     * @param {number} modifierDisplayGroupId - The modifier display group to match.
     * @param {number} modifierId - The specific modifier ID to retrieve.
     * @returns {ICustomizeItemEditItemModifierMulti | null}
     *          The editable modifier data, or null when no matching modifiers exist.
     */
    private _getEditItemModifierInfoMulti(modifierDisplayGroupId: number, modifierId: number): ICustomizeItemEditItemModifierMulti | null {
        const [firstItem, secondItem] = this._item.itemsToEdit;
        const nonePreModifierId = this._item.nonePreModifierId;

        const first = this._findModifier(firstItem, modifierDisplayGroupId, modifierId, nonePreModifierId, HalfModifierSelection.LEFT);
        const second = this._findModifier(secondItem, modifierDisplayGroupId, modifierId, nonePreModifierId, HalfModifierSelection.RIGHT);

        // Nothing found
        if (!first && !second)
            return null;

        const result: ICustomizeItemEditItemModifierMulti = {
            id: modifierId,
            modifierDisplayGroupId: modifierDisplayGroupId,
            modifierData: new Map([])
        };

        // Single item case (whole item)
        if (this._item.itemsToEdit.length == 1 && first) {
            result.modifierData.set(HalfModifierSelection.WHOLE, {
                preModifierId: first.PreModifierId,
                modifier: { ...first }
            });
            return result;
        }

        const leftParams = this._buildModifierParams(first, second!, nonePreModifierId);
        const rightParams = this._buildModifierParams(second, first!, nonePreModifierId);

        result.modifierData.set(HalfModifierSelection.LEFT, first ? leftParams : null);
        result.modifierData.set(HalfModifierSelection.RIGHT, second ? rightParams : null);

        return result;
    }

    /**
     * Find a modifier on an order item that matches group + id and is valid.
     *
     * @param {IOrderItem} item - The order item containing modifiers.
     * @param {number} modifierDisplayGroupId - The display group ID to match.
     * @param {number} modifierId - The modifier ID to match.
     * @param {number} nonePreModifierId - The "none" pre-modifier identifier.
     * @returns {IOrderItemModifier | undefined} The found modifier, or undefined.
     */
    private _findModifier(
        item: IOrderItem | undefined,
        modifierDisplayGroupId: number,
        modifierId: number,
        nonePreModifierId: number,
        halfId: HalfModifierSelection
    ): IOrderItemModifier | undefined {
        if (!item)
            return undefined;

        const halfData = this._getHalf(halfId);
        if (item.ItemId !== halfData.item.ItemId)
            return undefined;

        return item.Modifiers.find(x =>
            x.ModifierDisplayGroupId === modifierDisplayGroupId &&
            x.ModifierId === modifierId &&
            (x.IsDefault || x.PreModifierId !== nonePreModifierId)
        );
    }

    /**
     * Build editable modifier params for half/whole selection.
     *
     * @param {IOrderItemModifier | undefined} primary - The preferred modifier (first choice).
     * @param {IOrderItemModifier} fallback - The fallback modifier if primary is missing.
     * @param {number} nonePreModifierId - ID to use when the modifier has no premode ID.
     * @returns {{ preModifierId: number; modifier: IOrderItemModifier }} The edit params.
     */
    private _buildModifierParams(
        primary: IOrderItemModifier | undefined,
        fallback: IOrderItemModifier,
        nonePreModifierId: number
    ): { preModifierId: number; modifier: IOrderItemModifier } {
        const chosen = primary ?? fallback;
        const preModifierId = primary?.PreModifierId ?? nonePreModifierId;

        return {
            preModifierId,
            modifier: { ...chosen, PreModifierId: preModifierId }
        };
    }

    /**
     * Validates the item configuration for both halves (if applicable) and checks if it is complete.
     * @param {IOrderItem} oItem1 - The first half or full item.
     * @param {IOrderItem} oItem2 - The second half item, if applicable.
     * @param {IDataModifierDisplayGroup[]} modifierDisplayGroups - The list of modifier display groups.
     * @returns {boolean} True if the item configuration is valid, false otherwise.
     */
    private _validateItemConfiguration(oItem1: IOrderItem, oItem2: IOrderItem | null, modifierDisplayGroups: IDataModifierDisplayGroup[]): { success: boolean, message?: string } {
        let halfChecked1 = { success: true } as { success: boolean, message?: string };
        let halfChecked2 = { success: true } as { success: boolean, message?: string };

        if (this.isHalfHalf() && oItem2) {
            halfChecked1 = this._isItemComplete(this._context, this._localizationService, this.getMenuItem(HalfModifierSelection.LEFT), oItem1, modifierDisplayGroups, true);
            halfChecked2 = this._isItemComplete(this._context, this._localizationService, this.getMenuItem(HalfModifierSelection.RIGHT), oItem2, modifierDisplayGroups, halfChecked1.success);
        } else
            halfChecked1 = this._isItemComplete(this._context, this._localizationService, this.getMenuItem(HalfModifierSelection.LEFT), oItem1, modifierDisplayGroups, true, null);

        return { success: halfChecked1.success && halfChecked2.success, message: halfChecked1.message ?? halfChecked2.message };
    }

    private _isItemComplete(
        context: Context,
        localizationService: LocalizationService,
        mItem: IDataItem | null,
        oItem: IOrderItem,
        modifierDisplayGroups: IDataModifierDisplayGroup[],
        displayErrorMessage: boolean,
        msg: string | null = null
    ): { success: boolean, message?: string } {
        if (!mItem || !mItem.ModifierDisplayGroups)
            return { success: !!mItem };

        for (const iMdg of mItem.ModifierDisplayGroups) {
            const mdg = modifierDisplayGroups.find(x => x.ModifierDisplayGroupId === iMdg.ModifierDisplayGroupId);
            if (!mdg?.IsVisible)
                continue;

            const modCount = this._getModifierCount(oItem, mdg);

            if (modCount >= mdg.MinSelectionCount)
                continue;

            if (displayErrorMessage) {
                const message = msg ??
                    localizationService.names("REQUIRED_MODIFIER_GROUP_MINIMUM")
                        .replace("{amount}", mdg.MinSelectionCount.toString())
                    .replace("{display_group}", this._localizationService.GetName(mdg.Names));
                return { success: false, message };
            }

            return { success: false };
        }

        return { success: true };
    }

    private _getModifierCount(oItem: IOrderItem, modifierDisplayGroup: IDataModifierDisplayGroup): number {
        let modCount = 0;
        for (const mod of oItem.Modifiers) {
            if (mod.ModifierDisplayGroupId === modifierDisplayGroup.ModifierDisplayGroupId) {
                modCount += 1;
                if (modCount >= modifierDisplayGroup.MinSelectionCount)
                    break;
            }
        }
        return modCount;
    }

    /**
     * Handles coupon adjustments when editing an existing item.
     * @returns {Promise<IRemovedCoupon[]>} The list of removed coupons.
     */
    private async _handleEditingCouponsAsync(): Promise<IRemovedCoupon[]> {
        const currentItem = this._item.itemsToEdit[0];
        const couponKeys = this._order.getProperty("ItemsCoupons").filter(x => x.ItemKey == currentItem.ItemKey).map(x => x.CouponKey);

        const redeemItemCouponKey = this._order.getProperty("Coupons").filter(x => couponKeys.includes(x.CouponKey)).find(x => x.CouponId == CouponIdList.LOYALTY_REDEEM_ITEM);

        if (redeemItemCouponKey) {
            const loyaltyItems = this._data.getBrandProperty("LoyaltyItems");
            const points = OnlineOrderingUtil.GetLoyaltyPoints(currentItem.ItemId, currentItem.SizeId, loyaltyItems);
            await OnlineOrderingUtil.redeemPointsAsync(points, currentItem.ItemKey);
        }

        return this._order.removeCouponByItemKeyAsync(this._profileService.loyaltyProvider, currentItem.ItemKey);
    }

    /**
     * Prepares an order item for addition to the order.
     * @param {ICustomizeItemBaseItem} baseItem - The base item data.
     * @param {number} itemKey - The key of the item.
     * @param {number} halfIndex - The half index (1 for first half, 2 for second half).
     * @returns {IOrderItem} The prepared order item.
     */
    private _prepareOrderItem(
        baseItem: ICustomizeItemBaseItem,
        itemKey: number,
        halfIndex: number,
        itemRecId?: number
    ): IOrderItem {
        let recId = this._order.getItemRecordId(itemRecId ? [itemRecId] : []);
        if (this.isEditing()) {
            if (halfIndex == 1 || halfIndex == 0)
                recId = this._item.itemsToEdit[0].ItemRecId;
            else if (halfIndex == 2)
                recId = this._item.itemsToEdit[1]?.ItemRecId ?? recId;
        }

        const menuItem = ([0, 1].includes(halfIndex) ? this.getMenuItem(HalfModifierSelection.LEFT) : this.getMenuItem(HalfModifierSelection.RIGHT));

        const item = this._order._createOrderItem(
            menuItem,
            this._item.sizeId,
            halfIndex,
            this._item.quantity,
            baseItem.Modifiers,
            baseItem.BeforeTaxDiscount,
            baseItem.AfterTaxDiscount
        );

        item.HalfHalfPrice = baseItem.HalfHalfPrice;

        item.BeforeTaxDiscount = 0;
        item.AfterTaxDiscount = 0;
        item.DiscountedMarkedQuantity = 0;
        item.DiscountedQuantity = 0;

        item.ItemKey = itemKey;
        item.ItemRecId = recId;
        item.HalfIndex = halfIndex;
        item.HalfCount = this.isHalfHalf() ? 2 : 1;
        item.Edit = true;
        item.Instructions = this._item.instructions || [];

        this._prepareEditedOrderItem(item, halfIndex);

        OnlineOrderingUtil.TaxItem(
            menuItem,
            item,
            this._order._getItemTaxDataDependencies()
        );

        return item;
    }

    private _prepareEditedOrderItem(item: IOrderItem, halfIndex: number): void {
        if (!this.isEditing())
            return;

        const first = this._item.itemsToEdit[0];

        if (halfIndex === 2) {
            const second = this._item.itemsToEdit[1];
            if (second) {
                item.ItemKey = second.ItemKey;
                item.ItemRecId = second.ItemRecId;
            } else {
                item.ItemKey = first.ItemKey;
            }
        } else {
            item.ItemKey = first.ItemKey;
            item.ItemRecId = first.ItemRecId;

            if (item.SizeId !== first.SizeId) {
                item.IsRedeemed = false;
            }
        }
    }

    /**
     * Finalizes the addition of items to the order by updating the cart and closing dialogs.
     * @param {IOrderItem[]} items - The list of items to add to the order.
     */
    private async _finalizeOrderItems(item: IOrderItem, item2?: IOrderItem): Promise<boolean> {
        const items = (this.isHalfHalf() && item2) ? [item, item2] : [item];
        const added = await this._order.addItems(this._profileService.loyaltyProvider, items);
        if (!added)
            return false;

        if (this.mealDeal) {
            this.mealDeal.addToDealCallback(this.mealDeal.selectedGroup, items, this.isEditing());
        }
        return true;
    }

    /**
     * Handles adjustments to the order items and coupons after editing an existing item.
     * @param {IOrderItem} item - The first order item.
     * @param {IOrderItem | null} item2 - The second order item, if applicable.
     * @param {IRemovedCoupon[]} removedCouponIds - The list of removed coupons.
     */
    private async _handlePostEditingAdjustmentsAsync(item: IOrderItem, removedCouponIds: IRemovedCoupon[]): Promise<void> {
        if (this._item.itemsToEdit.length === 2 && !this.isHalfHalf())
            this._order.removeItemByItemRecId(this._item.itemsToEdit[1].ItemRecId);

        for (const coupon of removedCouponIds) {
            if (coupon.couponId === CouponIdList.LOYALTY_REDEEM_ITEM) {
                const loyaltyItems = this._data.getBrandProperty("LoyaltyItems");
                const points = OnlineOrderingUtil.GetLoyaltyPoints(item.ItemId, item.SizeId, loyaltyItems);
                item.IsRedeemed = false;
                await OnlineOrderingUtil.redeemPointsAsync(points, item.ItemKey);
            } else {
                const params: IApplyCouponByIdParamsPartial = {
                    loyaltyProvider: this._profileService.loyaltyProvider,
                    couponId: coupon.couponId,
                    rewardId: coupon.rewardId,
                    isBankedCurrency: coupon.isBankedCurrency,
                    showErrorMsg: true,
                    couponCode: coupon.offerCode,
                    isCampaignCode: coupon.isCampaignCode,
                    isPromotionCode: coupon.isPromotionCode,
                    channelId: coupon.channelId,
                    sourceId: coupon.sourceId,
                    isSUCValidationReq: Coupon.SucValidationNeeded(coupon.isPromotionCode, coupon.offerCode),
                    isSpecialCode: coupon.isSpecialCode,
                    isSingleUseCoupon: coupon.isSingleUseCoupon
                };
                await this._order.applyCouponByIdAsync(params);
            }
        }
    }

    private _itemGetDefaultModifiers(mItem: IDataItem, sizeId: number | null, modifierDisplayGroups: IDataModifierDisplayGroup[], defaultPreModifierId: number): IOrderItemModifier[] {
        if (!mItem.ModifierDisplayGroups)
            return [];

        const mods: IOrderItemModifier[] = [];
        for (const [index, mdg] of mItem.ModifierDisplayGroups.entries()) {
            let modAddedForThisMDG = false;

            for (const [index2, iMod] of mdg.Modifiers.entries()) {
                if (!iMod.IsDefault)
                    continue;

                const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === iMod.ModifierId);

                if (OnlineOrderingUtil.isModOfferedOnSize(iMod, sizeId ?? 0)) {
                    const mod = {
                        ModifierId: iMod.ModifierId,
                        Name: iMod.Name,
                        Index: iMod.Index,
                        ModifierDisplayGroupIndex: mdg.Index,
                        iIndex: index2,
                        iModifierDisplayGroupIndex: index,
                        PreModifierId: defaultPreModifierId,
                        IsDefault: iMod.IsDefault,
                        ModifierDisplayGroupId: mdg.ModifierDisplayGroupId,
                        MergedModifierDisplayGroupId: iMod.MergedModifierDisplayGroupId,
                        CountForPricing: iMod.CountForPricing,
                        CountForPricingByItself: iMod.CountForPricingByItself,
                        Price: 0,
                        IsCrust: modifierData?.IsCrust ?? false
                    };
                    mods.push(mod);
                } else {
                    // Search for compatible mod
                    for (const [index3, iMod2] of mdg.Modifiers.entries()) {
                        const compatibleMod = OnlineOrderingUtil.isModOfferedOnSize(iMod2, sizeId ?? 0);
                        if (compatibleMod) {
                            const mod = {
                                ModifierId: iMod2.ModifierId,
                                Name: iMod2.Name,
                                Index: iMod2.Index,
                                ModifierDisplayGroupIndex: mdg.Index,
                                iIndex: index3,
                                iModifierDisplayGroupIndex: index,
                                PreModifierId: defaultPreModifierId,
                                IsDefault: iMod2.IsDefault,
                                ModifierDisplayGroupId: mdg.ModifierDisplayGroupId,
                                MergedModifierDisplayGroupId: iMod2.MergedModifierDisplayGroupId,
                                CountForPricing: iMod2.CountForPricing,
                                CountForPricingByItself: iMod2.CountForPricingByItself,
                                Price: 0,
                                IsCrust: modifierData?.IsCrust ?? false
                            };
                            mods.push(mod);
                            break;
                        }
                    }
                }
                modAddedForThisMDG = true;
            }

            if (!modAddedForThisMDG) {
                const dMdg = modifierDisplayGroups.find(x => x.ModifierDisplayGroupId == mdg.ModifierDisplayGroupId);
                if (dMdg && (dMdg.MaxSelectionCount === 1 || dMdg.MustToggle)) {
                    const iMod = mdg.Modifiers[0];
                    const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === iMod.ModifierId);

                    const mod = {
                        ModifierId: iMod.ModifierId,
                        Name: iMod.Name,
                        Index: iMod.Index,
                        ModifierDisplayGroupIndex: mdg.Index,
                        iIndex: 0,
                        iModifierDisplayGroupIndex: index,
                        PreModifierId: defaultPreModifierId,
                        IsDefault: iMod.IsDefault,
                        ModifierDisplayGroupId: mdg.ModifierDisplayGroupId,
                        MergedModifierDisplayGroupId: iMod.MergedModifierDisplayGroupId,
                        CountForPricing: iMod.CountForPricing,
                        CountForPricingByItself: iMod.CountForPricingByItself,
                        Price: 0,
                        IsCrust: modifierData?.IsCrust ?? false
                    };
                    mods.push(mod);
                }
            }
        }

        return mods;
    }

    private _getItemPriceForSize(prices: IItemPrice[], sizeId: number | null): number {
        if (!prices)
            return 0;

        const item = prices.find(item => item.SizeId === sizeId);
        return item ? item.MinimumPrice : 0;
    }

    /**
     * Calculates and returns the calorie information for a modifier.
     * @param {IItemModifierDisplayGroupModifier} itemModifier - The item modifier data.
     * @param {IDataModifier} modifier - The data modifier details.
     * @returns {string | null} The formatted calorie text or null if unavailable.
     */
    private _getModifierCalories(menuItem: IDataItem, itemModifier: IItemModifierDisplayGroupModifier, modifier: IDataModifier): string | null {
        const servingCalories = OnlineOrderingUtil.getItemSizeMinMaxCalories(menuItem, this._item.sizeId);
        const servingName = (menuItem.ServingNames) ? this._localizationService.GetName(menuItem.ServingNames) : this._localizationService.names("Slice");

        let calories: string | null = null;
        const description = this._localizationService.GetDescription(modifier.Descriptions);
        calories = this._getModifierCaloriesDescription(description);

        if (!calories) {
            const count = Math.max(OnlineOrderingUtil.getMenuItemModCount(menuItem, itemModifier.MergedModifierDisplayGroupId), 1);
            const calorieVal = OnlineOrderingUtil.getMenuItemModCalories(
                itemModifier,
                this._item.sizeId,
                count,
                this._item.defaultPreModifierId,
                this._data.getProperty("Settings").PMODDM,
                this._data.getProperty("PreModifiers"),
                this._item.defaultPreModifierId
            );

            if (calorieVal > 0) {
                const perServing = Math.ceil(calorieVal / servingCalories.serves);
                const calDisplayId = menuItem.CalorieDisplayId;

                if (calDisplayId === CalorieDisplay.SERVING)
                    calories = this._localizationService.names("CaloriesPerServing")
                        .replace("{{calorie}}", perServing.toString())
                        .replace("{{servingName}}", servingName);
                else if (calDisplayId === CalorieDisplay.FRACTION)
                    calories = this._localizationService.names("CaloriesPerFraction")
                        .replace("{{calorie}}", perServing.toString())
                        .replace("{{serving}}", servingCalories.serves.toString())
                        .replace("{{servingName}}", servingName);
            }
        }

        return calories;
    }

    private _getModifierCaloriesDescription(desc: string): string {
        return desc.includes("cal.") ? desc : "";
    }

    /**
     * Returns a formatted price string for a specific modifier, suitable for UI display.
     *
     * The value represents the price delta contributed by the modifier only
     * (not the full item price). If the modifier does not add any cost, an empty
     * string is returned.
     *
     * @param {IDataItem} menuItem - The menu item the modifier belongs to.
     * @param {number} modifierId - The modifier identifier.
     * @param {number} modifierDisplayGroupId - The modifier display group identifier.
     * @returns {string} A localized, formatted price string (e.g. "$1.50"),
     *                   or an empty string if the modifier is not chargeable.
     */
    private _getModifierPriceDisplayText(menuItem: IDataItem, modifierId: number, modifierDisplayGroupId: number): string {
        if (this._data.getProperty("Settings").OOS.customizeItemMode === CustomizeItemUiOption.MULTI_ITEM)
            return "";

        const country = this._data.getProperty("StoreInfo").country;
        const orderPriceGroupId = this._order.getProperty("OrderTypeSubType").PriceGroupId;
        const defaultPriceGroupId = this._data.getProperty("DefaultPriceGroupId");
        const nonChargeableDisplayGroups = this._data.getProperty("Settings").NCDG;
        const preModifiers = this._data.getProperty("PreModifiers");

        const price = this._getModifierPrice({
            mItem: menuItem,
            sizeId: this._item.sizeId,
            modifierId,
            modifierDisplayGroupId,
            orderPriceGroupId,
            defaultPriceGroupId,
            nonChargeableDisplayGroups,
            defaultPreModifierId: this._item.defaultPreModifierId,
            preModifiers
        });

        return price > 0
            ? Util.formatMoney(price, country)
            : "";
    }

    /**
     * Calculates the price delta contributed by a single modifier.
     *
     * This function returns only the modifier's price impact, not the full item price.
     * Pricing rules include per-modifier pricing, pre-modifier multipliers, and
     * "adds to price" settings. Item-level minimums, maximums, and caps are intentionally
     * excluded.
     *
     * @param {Object} params - Modifier pricing parameters.
     * @param {IDataItem} params.mItem - The menu item the modifier belongs to.
     * @param {number} params.sizeId - The selected item size identifier.
     * @param {number} params.modifierId - The modifier identifier.
     * @param {number} params.modifierDisplayGroupId - The modifier display group identifier.
     * @param {number} params.orderPriceGroupId - The active order price group identifier.
     * @param {number} params.defaultPriceGroupId - The default price group identifier.
     * @param {string[]} params.nonChargeableDisplayGroups - Display group IDs that suppress modifier charges.
     * @param {number} params.defaultPreModifierId - The default pre-modifier identifier.
     * @param {IDataPreModifier[]} params.preModifiers - Available pre-modifier definitions.
     *
     * @returns {number} The calculated modifier price delta, rounded to two decimals.
     */
    private _getModifierPrice(params: {
        mItem: IDataItem,
        sizeId: number,
        modifierId: number,
        modifierDisplayGroupId: number,
        orderPriceGroupId: number,
        defaultPriceGroupId: number,
        nonChargeableDisplayGroups: string[],
        defaultPreModifierId: number,
        preModifiers: IDataPreModifier[]
    }): number {
        // ---- resolve item price (same logic as full calc)
        let iPrice =
            params.mItem.Prices.find(x => x.PriceGroupId === params.orderPriceGroupId && x.SizeId === params.sizeId) ??
            params.mItem.Prices.find(x => x.PriceGroupId === params.defaultPriceGroupId && x.SizeId === params.sizeId) ??
            params.mItem.Prices.find(x => x.SizeId === params.sizeId);

        if (!iPrice)
            return 0;

        // ---- resolve modifier + display group
        const modDispGroup = params.mItem.ModifierDisplayGroups.find(
            x => x.ModifierDisplayGroupId === params.modifierDisplayGroupId
        );
        if (!modDispGroup)
            return 0;

        const mod = modDispGroup.Modifiers.find(x => x.ModifierId === params.modifierId);
        if (!mod)
            return 0;

        // ---- pre-modifier logic
        const preMod = OnlineOrderingUtil.GetPreMod(params.defaultPreModifierId, params.preModifiers);
        let preModMultiplier = 1;
        let pCount = 0;

        if (preMod) {
            preModMultiplier = Number(preMod.PriceCountMultiplier);

            if (params.nonChargeableDisplayGroups?.includes(`-${params.modifierDisplayGroupId}-`))
                preModMultiplier = 1;

            if (mod.CountForPricing)
                pCount = preModMultiplier;
            else if (preModMultiplier > 1)
                pCount = preModMultiplier - 1;
        }

        if (pCount < 0)
            pCount = 0;

        // ---- base per-modifier price
        let price = pCount * Number(iPrice.PerModifierPrice);

        // ---- adds-to-price logic
        if (mod.AddsToPrice) {
            const addToPrice = preModMultiplier * OnlineOrderingUtil.GetModifierAddToPrice(mod, params.sizeId);

            price += addToPrice;
        }

        // round exactly like item pricing
        return Number((Math.round(price * 100) / 100).toFixed(2));
    }

    private _applyModifierUpdate(
        modifierId: number,
        itemModifier: IItemModifierDisplayGroupModifier,
        modifierDisplayGroup: IDataModifierDisplayGroup,
        preModifierId: number,
        halfId: HalfModifierSelection,
        removeModifier: boolean
    ): boolean {
        if (removeModifier && !itemModifier.IsDefault) {
            this._updateModifier({ halfId, modifierId, isSelected: false, modifier: null });
            return true;
        }

        return this._updateSelectedModifier(itemModifier, modifierDisplayGroup, modifierId, preModifierId, halfId, removeModifier);
    }

    private _validateFinalItemPrice(modifierId: number): boolean {
        const oItems = this._getItems();
        for (const oItem of oItems) {
            const menuItem = this.getMenuItem(oItem.halfId);

            // This is a max-modifier-count check, not the authoritative pricing guard (that's at
            // add-to-cart time) -- a transient pricing failure here shouldn't block the modifier edit.
            let price: number;
            try {
                price = this._order._getItemPrice(menuItem, this._item.sizeId, oItem.item.Modifiers).Price;
            } catch (e) {
                if (!(e instanceof ItemPricingUnavailableError))
                    throw e;
                continue;
            }

            if (price < 0) {
                const { left, right, isWhole } = this._getModifierData(modifierId);
                const revertHalfId = isWhole ? HalfModifierSelection.WHOLE : oItem.halfId;
                const isDefault = (oItem.halfId === HalfModifierSelection.RIGHT ? right?.isDefault : left?.isDefault) ?? false;
                const preModifierId = isDefault ? this._item.nonePreModifierId : undefined;
                const modifier = isDefault ? undefined : null;

                this._updateModifier({ halfId: revertHalfId, modifierId, preModifierId, isSelected: false, modifier });

                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("Error_ReachedMaxNumberOfModifiers"),
                    true
                );
                return false;
            }
        }
        return true;
    }

    /**
     * Determines the correct item key based on current editing state and meal deal.
     *
     * @private
     * @returns {number} The resolved item key.
     */
    private _determineItemKey(): number {
        if (this.mealDeal && this.mealDeal.itemKey > -1)
            return this.mealDeal.itemKey;

        if (this.isEditing())
            return this._item.itemsToEdit[0].ItemKey;

        return this._order.getNextItemKey();
    }

    /**
     * Determines whether two modifiers represent the same selection
     * and should collapse into a single WHOLE modifier state.
     *
     * @private
     * @param {CustomizeItemModifier} left - The left half modifier.
     * @param {CustomizeItemModifier} right - The right half modifier.
     * @returns {boolean} True if both halves are equivalent and should merge.
     */
    private _shouldCollapseToWhole(left: CustomizeItemModifier, right: CustomizeItemModifier): boolean {
        return (
            left.isSelected === right.isSelected &&
            left.preModifierId === right.preModifierId
        );
    }
}